import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { useAutopilot, AutopilotStep2Panel, AutopilotStep3Panel } from "../components/commissions/AutopilotCard";
import {
  Upload,
  Download,
  FileSpreadsheet,
  Loader2,
  Wallet,
  History,
  AlertTriangle,
  Trash2,
  Mail,
  FileText,
  Send,
  Plus,
  RefreshCw,
  CheckCircle2,
  Clock,
  Lock,
  Unlock,
  X,
  Search,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { downloadKarvyPreview } from "../lib/karvyPreview";
import ClientWise from "./ClientWise";
import { useAuth } from "../context/AuthContext";
import JSZip from "jszip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB — well under typical ingress request limits
const POLL_INTERVAL_MS = 4000;
const POLL_MAX_TRANSIENT_ERRORS = 30;       // ~2 minutes of 502/503/504 grace
const POLL_TRANSIENT_STATUSES = [0, 408, 429, 500, 502, 503, 504, 522, 524];
const LS_KEY = "commissions_active_job_id";

const formatBytes = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(1)} ${u[i]}`;
};

const formatDateTime = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return s;
  }
};

const newJobId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, "");
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
};

const FileSlot = ({ label, hint, file, onChange, disabled, testId, uploadedPct, extraAction }) => (
  <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4">
    <div className="flex items-center justify-between">
      <Label className="text-sm font-semibold text-slate-700">{label}</Label>
      {file ? (
        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
          {formatBytes(file.size)}
        </Badge>
      ) : null}
    </div>
    <p className="text-xs text-slate-500">{hint}</p>
    <Input
      type="file"
      accept=".xlsx,.xls"
      disabled={disabled}
      onChange={(e) => onChange(e.target.files?.[0] || null)}
      data-testid={testId}
      className="cursor-pointer"
    />
    {file && (
      <p className="truncate text-xs text-slate-600" title={file.name}>
        <FileSpreadsheet className="inline h-3.5 w-3.5 mr-1" />
        {file.name}
      </p>
    )}
    {typeof uploadedPct === "number" && uploadedPct > 0 && (
      <Progress value={uploadedPct} className="h-1.5" />
    )}
    {extraAction ? <div className="pt-1">{extraAction}</div> : null}
  </div>
);

// Prepare VOLT files with a strict Process→Download flow:
//   1. User clicks Process VOLT.
//   2. Backend gaps modal appears if any IFA mapping is missing (searchable
//      dropdown from associate master).
//   3. Once all gaps are saved, both Broker + Kinntegra workbooks are fetched
//      and buffered client-side.
//   4. Two download buttons appear: Broker (also attaches file to VOLT slot)
//      and Kinntegra. No downloads are possible until Process completes.
// KARVY prepare — mirrors the VOLT flow but reads all MFSD205_Raw rows and
// uses Account Number as the gap-fill key.
const AutoPrepareKarvyButton = ({ disabled, onLoaded, onAutoStep2 }) => {
  const [stage, setStage] = useState("idle"); // idle | processing | ready | mapping
  const [gapModal, setGapModal] = useState({ open: false, gaps: [] });
  const [savingGaps, setSavingGaps] = useState(false);
  const [associates, setAssociates] = useState([]);
  const [file, setFile] = useState(null);

  const fetchAssociates = async () => {
    if (associates.length) return associates;
    try {
      const token = localStorage.getItem("token");
      const resp = await axios.get(`${API}/commissions/prepare/volt/associates`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const list = resp.data?.associates || [];
      setAssociates(list);
      return list;
    } catch { return []; }
  };

  const parseGaps = async (err) => {
    try {
      const r = err?.response;
      if (!r || r.status !== 409) return null;
      let data = r.data;
      if (data instanceof Blob) data = JSON.parse(await data.text());
      if (data?.detail?.error === "unmapped_subbroker") return data.detail.gaps || [];
    } catch { /* */ }
    return null;
  };
  const errMsg = async (err) => {
    let d = err?.response?.data;
    if (d instanceof Blob) { try { d = JSON.parse(await d.text()); } catch { d = null; } }
    if (!d) return err.message;
    return typeof d === "string" ? d : (d.detail?.error || d.detail || err.message);
  };
  const saveBlob = (blob, fname) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const doProcess = async () => {
    setStage("processing");
    try {
      const token = localStorage.getItem("token");
      const resp = await axios.post(`${API}/commissions/prepare/karvy/dump-to-mfsd205`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const d = resp.data || {};
      toast.success(`Dumped ${d.inserted} rows to MFSD205 · period ${d.period}`);
      setStage("ready");
    } catch (err) {
      const gaps = await parseGaps(err);
      if (gaps && gaps.length) {
        const assoc = await fetchAssociates();
        setGapModal({ open: true, gaps: gaps.map((g) => ({ ...g, sub_broker: "" })) });
        setAssociates(assoc);
        setStage("mapping");
      } else {
        toast.error(`Process failed: ${await errMsg(err)}`);
        setStage("idle");
      }
    }
  };

  const updateGap = (idx, val) => {
    setGapModal((s) => {
      const g = s.gaps.slice();
      g[idx] = { ...g[idx], sub_broker: val };
      return { ...s, gaps: g };
    });
  };
  const allMapped = gapModal.gaps.length > 0 && gapModal.gaps.every((g) => (g.sub_broker || "").trim());
  const saveGapsAndContinue = async () => {
    if (!allMapped) { toast.error("Every row must be mapped"); return; }
    setSavingGaps(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/commissions/prepare/karvy/save-gaps`,
        { entries: gapModal.gaps.map((g) => ({
            account_number: g.account_number,
            sub_broker: g.sub_broker,
            investor_name: g.investor_name,
          })) },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      toast.success(`Saved ${gapModal.gaps.length} mapping(s)`);
      setGapModal({ open: false, gaps: [] });
      await doProcess();
    } catch (err) {
      toast.error(`Save failed: ${await errMsg(err)}`);
    } finally { setSavingGaps(false); }
  };

  const doPreviewDownload = async () => {
    const token = localStorage.getItem("token");
    const preparingToast = toast.loading("Starting KARVY preview build…");
    try {
      const result = await downloadKarvyPreview({
        apiUrl: process.env.REACT_APP_BACKEND_URL,
        token,
        onProgress: (msg) => toast.loading(msg, { id: preparingToast }),
      });
      toast.dismiss(preparingToast);
      if (result.gaps) {
        // Route sub-broker gaps into the tagging modal (VOLT-style flow).
        if (result.gapType === "unmapped_subbroker") {
          const assoc = await fetchAssociates();
          setGapModal({ open: true, gaps: result.gaps.map((g) => ({ ...g, sub_broker: "" })) });
          setAssociates(assoc);
          setStage("mapping");
          return;
        }
        toast.error(`${result.gaps.length} new Brokerage_Type(s) need tagging — tag them from Downloads → MFSD205 Preview`);
        return;
      }
      toast.success(`Preview: ${Number(result.row_count).toLocaleString()} rows · ${result.unmapped_ct} still without SubBroker`);
    } catch (err) {
      toast.dismiss(preparingToast);
      toast.error(`Preview failed: ${err.message}`);
    }
  };

  const anyBusy = disabled || stage === "processing" || stage === "mapping";

  return (
    <>
      {stage !== "ready" ? (
        <div className="space-y-2">
          <Button
            type="button" variant="outline" size="sm"
            onClick={doProcess} disabled={anyBusy}
            data-testid="commissions-karvy-process-btn"
            className="w-full border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
          >
            {stage === "processing" ? (<><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Processing…</>)
              : stage === "mapping" ? (<><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Awaiting mapping…</>)
              : (<><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Process KARVY</>)}
          </Button>
          <Button
            type="button" variant="outline" size="sm"
            onClick={doPreviewDownload} disabled={anyBusy}
            data-testid="commissions-karvy-preview-btn"
            className="w-full border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
          ><Download className="mr-1.5 h-3.5 w-3.5" /> Download MFSD205 Preview (empty cols)</Button>
        </div>
      ) : (
        <div className="space-y-2">
          <Button
            type="button" variant="outline" size="sm"
            onClick={() => file && saveBlob(file.blob, file.fname)}
            disabled={disabled}
            data-testid="commissions-karvy-download-btn"
            className="w-full border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
          ><Download className="mr-1.5 h-3.5 w-3.5" /> Download KARVY</Button>
          {onAutoStep2 && (
            <Button
              type="button" size="sm"
              onClick={() => file && onAutoStep2(new File([file.blob], file.fname, {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              }))}
              disabled={disabled}
              data-testid="commissions-karvy-autostep2-btn"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            ><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Auto-Run Step 2 (KARVY only)</Button>
          )}
          <button
            type="button"
            onClick={() => { setStage("idle"); setFile(null); }}
            disabled={disabled}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
          >Reprocess with fresh data</button>
        </div>
      )}
      <Dialog open={gapModal.open} onOpenChange={(o) => {
        if (!o) { setGapModal({ open: false, gaps: [] }); setStage("idle"); }
      }}>
        <DialogContent className="max-w-4xl" data-testid="commissions-karvy-gaps-modal">
          <DialogHeader>
            <DialogTitle>Map Sub-Broker ({gapModal.gaps.length} accounts)</DialogTitle>
            <DialogDescription>
              These MFSD205_Raw accounts either have no Sub-Broker or carry a code that
              doesn&apos;t match any associate in the <code>subbroker</code> collection,
              and the <code>subbroker_mapping</code> PAN chain couldn&apos;t resolve them
              either. Pick the correct associate for each — <b>all rows must be mapped</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] overflow-auto rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-xs font-semibold text-slate-600 z-10">
                <tr><th className="px-3 py-2 text-left">Investor</th>
                    <th className="px-3 py-2 text-left">Account #</th>
                    <th className="px-3 py-2 text-left">Raw code (RTA)</th>
                    <th className="px-3 py-2 text-left w-64">Associate (Sub-Broker)</th></tr>
              </thead>
              <tbody>
                {gapModal.gaps.map((g, i) => (
                  <tr key={g.account_number} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-800">{g.investor_name || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{g.account_number}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {g.raw_sub_broker
                        ? <span className="rounded bg-rose-50 px-1.5 py-0.5 text-rose-700"
                                title={(g.raw_sub_broker_examples || []).join(", ")}>
                            {g.raw_sub_broker}
                            {g.raw_sub_broker_examples && g.raw_sub_broker_examples.length > 1
                              ? ` +${g.raw_sub_broker_examples.length - 1}`
                              : ""}
                          </span>
                        : <span className="text-slate-400">(empty)</span>}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={g.sub_broker}
                        onChange={(e) => updateGap(i, e.target.value)}
                        data-testid={`karvy-gap-select-${i}`}
                        className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="">— select associate —</option>
                        {associates.map((a) => (
                          <option key={a.code} value={a.code}>{a.code} — {a.name || a.email || "(unnamed)"}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline"
              onClick={() => { setGapModal({ open: false, gaps: [] }); setStage("idle"); }}
              disabled={savingGaps} data-testid="karvy-gaps-cancel"
            >Cancel</Button>
            <Button onClick={saveGapsAndContinue} disabled={savingGaps || !allMapped}
              data-testid="karvy-gaps-save"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >{savingGaps ? (<><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Saving…</>)
              : allMapped ? "Save & Process" : "Map all rows to continue"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const AutoPrepareVoltButton = ({ disabled, onLoaded, onAutoStep2 }) => {
  const [stage, setStage] = useState("idle"); // idle | mapping | processing | ready
  const [gapModal, setGapModal] = useState({ open: false, gaps: [] });
  const [savingGaps, setSavingGaps] = useState(false);
  const [associates, setAssociates] = useState([]);
  const [files, setFiles] = useState({ broker: null, kinntegra: null });

  const fetchAssociates = async () => {
    if (associates.length) return associates;
    try {
      const token = localStorage.getItem("token");
      const resp = await axios.get(`${API}/commissions/prepare/volt/associates`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const list = resp.data?.associates || [];
      setAssociates(list);
      return list;
    } catch {
      return [];
    }
  };

  const fetchBlob = async (path, defaultSuffix) => {
    const token = localStorage.getItem("token");
    const resp = await axios.get(`${API}/commissions/prepare/${path}`, {
      responseType: "blob",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const cd = resp.headers["content-disposition"] || "";
    const m = /filename="?([^"]+)"?/i.exec(cd);
    const now = new Date();
    const fname = m ? m[1] : `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, "0")}_${defaultSuffix}.xlsx`;
    return {
      blob: resp.data,
      fname,
      period: resp.headers["x-volt-period-to"] || "?",
      rows: resp.headers["x-volt-row-count"] || "?",
    };
  };

  const parseGapsFromError = async (err) => {
    try {
      const r = err?.response;
      if (!r || r.status !== 409) return null;
      let data = r.data;
      if (data instanceof Blob) data = JSON.parse(await data.text());
      const detail = data?.detail;
      if (detail && typeof detail === "object" && detail.error === "unmapped_ifa") {
        return detail.gaps || [];
      }
    } catch { /* fall-through */ }
    return null;
  };

  const errMsg = async (err) => {
    let d = err?.response?.data;
    if (d instanceof Blob) {
      try { d = JSON.parse(await d.text()); } catch { d = null; }
    }
    if (!d) return err.message;
    return typeof d === "string" ? d : (d.detail?.error || d.detail || err.message);
  };

  const saveBlob = (blob, fname) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const doProcess = async () => {
    setStage("processing");
    try {
      // Fetch broker first — this triggers 409 if any gaps
      const broker = await fetchBlob("volt", "VOLT");
      const kinntegra = await fetchBlob("volt-kinntegra", "VOLT_Kinntegra");
      setFiles({ broker, kinntegra });
      setStage("ready");
      toast.success(`Processed ${broker.rows} rows for period ${broker.period}`);
      // Auto-chain into Step 2 with the freshly buffered Broker file. Manual
      // upload path is preserved (user can attach CAMS/KARVY separately and
      // hit "Process & Download ZIP" as before).
      if (onAutoStep2) {
        const file = new File([broker.blob], broker.fname, {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        onLoaded(file);
        toast.info("Auto-running Step 2 with the prepared Broker file…");
        // Fire-and-forget: parent's handleProcess manages its own busy state.
        onAutoStep2(file);
      }
    } catch (err) {
      const gaps = await parseGapsFromError(err);
      if (gaps && gaps.length) {
        const assoc = await fetchAssociates();
        setGapModal({
          open: true,
          gaps: gaps.map((g) => ({ ...g, ifa_code: "" })),
        });
        setAssociates(assoc);
        setStage("mapping");
      } else {
        toast.error(`Process failed: ${await errMsg(err)}`);
        setStage("idle");
      }
    }
  };

  const updateGapCode = (idx, val) => {
    setGapModal((s) => {
      const gaps = s.gaps.slice();
      gaps[idx] = { ...gaps[idx], ifa_code: val };
      return { ...s, gaps };
    });
  };

  const allMapped = gapModal.gaps.length > 0
    && gapModal.gaps.every((g) => (g.ifa_code || "").trim());

  const saveGapsAndContinue = async () => {
    if (!allMapped) {
      toast.error("Every customer must be mapped before continuing");
      return;
    }
    setSavingGaps(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/commissions/prepare/volt/save-gaps`,
        {
          entries: gapModal.gaps.map((g) => ({
            source_id: g.source_id,
            ifa_code: g.ifa_code,
            customer_name: g.customer_name,
          })),
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success(`Saved ${gapModal.gaps.length} IFA mapping(s)`);
      setGapModal({ open: false, gaps: [] });
      await doProcess();  // retry; if gaps remain (shouldn't) modal re-opens
    } catch (err) {
      toast.error(`Save failed: ${await errMsg(err)}`);
    } finally {
      setSavingGaps(false);
    }
  };

  const handleDownloadBroker = () => {
    if (!files.broker) return;
    saveBlob(files.broker.blob, files.broker.fname);
    const file = new File([files.broker.blob], files.broker.fname, {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    onLoaded(file);
  };

  const handleAutoStep2 = () => {
    if (!files.broker) return;
    const file = new File([files.broker.blob], files.broker.fname, {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    onLoaded(file);
    if (onAutoStep2) onAutoStep2(file);
  };

  const handleDownloadKinntegra = () => {
    if (!files.kinntegra) return;
    saveBlob(files.kinntegra.blob, files.kinntegra.fname);
  };

  const handleReset = () => {
    setStage("idle");
    setFiles({ broker: null, kinntegra: null });
  };

  return (
    <>
      {stage !== "ready" ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={doProcess}
          disabled={disabled || stage === "processing" || stage === "mapping"}
          data-testid="commissions-volt-process-btn"
          className="w-full border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
        >
          {stage === "processing" ? (
            <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Processing…</>
          ) : stage === "mapping" ? (
            <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Awaiting mapping…</>
          ) : (
            <><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Process VOLT</>
          )}
        </Button>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDownloadBroker}
              disabled={disabled}
              data-testid="commissions-volt-download-btn"
              className="border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
            >
              <Download className="mr-1.5 h-3.5 w-3.5" /> Broker
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDownloadKinntegra}
              disabled={disabled}
              data-testid="commissions-volt-kinntegra-btn"
              className="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
            >
              <Download className="mr-1.5 h-3.5 w-3.5" /> Kinntegra
            </Button>
          </div>
          {onAutoStep2 && (
            <Button
              type="button"
              size="sm"
              onClick={handleAutoStep2}
              disabled={disabled}
              data-testid="commissions-volt-autostep2-btn"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Auto-Run Step 2 (VOLT only)
            </Button>
          )}
          <button
            type="button"
            onClick={handleReset}
            disabled={disabled}
            data-testid="commissions-volt-reset-btn"
            className="text-xs text-slate-500 hover:text-slate-700 underline"
          >
            Reprocess with fresh data
          </button>
        </div>
      )}

      <Dialog
        open={gapModal.open}
        onOpenChange={(o) => {
          if (!o) {
            setGapModal({ open: false, gaps: [] });
            setStage("idle");
          }
        }}
      >
        <DialogContent className="max-w-3xl" data-testid="commissions-volt-gaps-modal">
          <DialogHeader>
            <DialogTitle>Map missing IFA associates ({gapModal.gaps.length})</DialogTitle>
            <DialogDescription>
              These customers in the latest VOLT_Raw period have no IFA
              mapping yet. Pick the correct associate for each row — <b>all
              rows must be mapped</b> before the Broker &amp; Kinntegra files
              can be generated.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] overflow-auto rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-xs font-semibold text-slate-600 z-10">
                <tr>
                  <th className="px-3 py-2 text-left">Customer</th>
                  <th className="px-3 py-2 text-left">source_id</th>
                  <th className="px-3 py-2 text-left w-64">Associate (IFA)</th>
                </tr>
              </thead>
              <tbody>
                {gapModal.gaps.map((g, i) => (
                  <tr key={g.source_id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-800">{g.customer_name || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500" title={g.source_id}>
                      {g.source_id.slice(0, 12)}…
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={g.ifa_code}
                        onChange={(e) => updateGapCode(i, e.target.value)}
                        data-testid={`volt-gap-select-${i}`}
                        className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="">— select associate —</option>
                        {associates.map((a) => (
                          <option key={a.code} value={a.code}>
                            {a.code} — {a.name || a.email || "(unnamed)"}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setGapModal({ open: false, gaps: [] });
                setStage("idle");
              }}
              disabled={savingGaps}
              data-testid="volt-gaps-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={saveGapsAndContinue}
              disabled={savingGaps || !allMapped}
              data-testid="volt-gaps-save"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {savingGaps ? (
                <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Saving…</>
              ) : allMapped ? (
                <>Save &amp; Process</>
              ) : (
                <>Map all rows to continue</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const defaultPeriodMonthYear = () => {
  // Default to the previous calendar month (commissions are typically
  // generated for the month that just ended).
  const now = new Date();
  let m = now.getMonth(); // 0-indexed; previous month = current - 1
  let y = now.getFullYear();
  if (m === 0) {
    m = 12; // December previous year
    y -= 1;
  }
  return { month: m, year: y };
};

const periodString = (month, year) => {
  if (!month || !year) return "";
  return `${year}_${String(month).padStart(2, "0")}`;
};

const periodLabel = (period) => {
  if (!period) return "";
  const [y, m] = period.split("_");
  const mi = parseInt(m, 10);
  if (!mi || mi < 1 || mi > 12) return period;
  return `${MONTH_LABELS[mi - 1]} ${y}`;
};

export default function Commissions() {
  const [cams, setCams] = useState(null);
  const [karvy, setKarvy] = useState(null);
  const [volt, setVolt] = useState(null);
  const _defaultPeriod = defaultPeriodMonthYear();
  const [periodMonth, setPeriodMonth] = useState(_defaultPeriod.month);
  const [periodYear, setPeriodYear] = useState(_defaultPeriod.year);
  const period = periodString(periodMonth, periodYear);
  const [stage, setStage] = useState("idle"); // idle | uploading | processing | downloading
  const [progressBySource, setProgressBySource] = useState({});
  const [jobMessage, setJobMessage] = useState("");
  const [processSource, setProcessSource] = useState(null); // "auto" | "manual" — which tab kicked off the current pipeline
  const [runs, setRuns] = useState([]);
  const ranOnce = useRef(false);
  const autopilotRef = useRef(null);
  const [step2Hidden, setStep2Hidden] = useState(false);
  const pollRef = useRef(null);
  // Aborts an in-flight Prepare Commission Files run. Signal is threaded
  // through every axios/fetch call inside handlePrepareCommissionFiles;
  // controller.abort() also cancels the KARVY GridFS build job on the
  // server via POST /commissions/cancel-job/{jobId}.
  const prepareAbortRef = useRef(null);
  const prepareJobIdRef = useRef(null);
  const [watchlistTab, setWatchlistTab] = useState("current"); // "current" | "historical"
  const [uploadMode, setUploadMode] = useState("auto"); // "auto" | "manual"
  const [autoPeriod, setAutoPeriod] = useState(null); // { period, source, per_source }
  const [autoPeriodLoading, setAutoPeriodLoading] = useState(false);
  const [karvyDownloading, setKarvyDownloading] = useState(false);
  const [karvyDownloadMsg, setKarvyDownloadMsg] = useState("");
  const [voltDownloading, setVoltDownloading] = useState(false);
  const [voltDownloadMsg, setVoltDownloadMsg] = useState("");
  const [camsDownloading, setCamsDownloading] = useState(false);
  const [camsDownloadMsg, setCamsDownloadMsg] = useState("");
  const [prepareBusy, setPrepareBusy] = useState(false);
  const [prepareMsg, setPrepareMsg] = useState("");
  // Per-source live progress feed for the CAMS / KARVY / VOLT tiles.
  // {percent, message, elapsed} — populated by the polling loops inside
  // handlePrepareCommissionFiles so each tile shows its own "70% · Building
  // xlsx workbook · 42s" line instead of a shared bar.
  const [prepareProgress, setPrepareProgress] = useState({
    karvy: { percent: 0, message: "", elapsed: 0 },
    volt:  { percent: 0, message: "", elapsed: 0 },
    cams:  { percent: 0, message: "", elapsed: 0 },
  });
  // Per-source prepare status. Drives the KARVY / VOLT / CAMS tiles below:
  // 'idle'    → button says Download (uses whatever's already cached)
  // 'running' → tile disabled, spinner + "Building…"
  // 'ready'   → button green, Download enabled
  // 'skipped' → tile grey; source had no raw data in Step 1
  // 'error'   → red tint; user can retry via Prepare
  const [prepareStatus, setPrepareStatus] = useState({
    karvy: "idle", volt: "idle", cams: "idle",
  });
  // Per-source last-rebuild timestamps (ISO strings). Populated by
  // hydratePrepareStatus on mount and refreshed at the tail of every
  // Prepare Commission Files run so the tiles can show a "Last rebuilt"
  // line. Falls back to null while idle / not prepared.
  const [prepareBuiltAt, setPrepareBuiltAt] = useState({
    karvy: null, volt: null, cams: null,
  });
  // KARVY sub-broker gap tagging modal used by the Prepare Commission
  // Files flow. When /dump-to-mfsd205 or /preview-download/start returns
  // 409 unmapped_subbroker we open this modal (instead of the confusing
  // "Failed" tile), let the operator tag every gap, save the mappings,
  // then automatically retry the full Prepare flow.
  const [karvyGapModal, setKarvyGapModal] = useState({ open: false, gaps: [] });
  const [karvyAssociates, setKarvyAssociates] = useState([]);
  const [karvySavingGaps, setKarvySavingGaps] = useState(false);
  // VOLT sub-broker gap tagging modal — mirrors the KARVY flow but keys
  // on source_id (VOLT_Raw's per-customer id) and posts to /prepare/volt/save-gaps.
  const [voltGapModal, setVoltGapModal] = useState({ open: false, gaps: [] });
  const [voltSavingGaps, setVoltSavingGaps] = useState(false);
  // CAMS preview cache state — the enriched workbook is streamed directly by
  // `downloadCamsPreviewExcel`. Every WBR77_Raw row is auto-enriched at ingest,
  // so there's no manual "enrich" button in the UI — the /backfill endpoint
  // still exists for one-off recovery after a fresh BSE Scheme Master upload.
  // `camsGapModal` powers the unresolved-folio tagging modal.
  const [camsGapModal, setCamsGapModal] = useState({ open: false, rows: [], options: [], loading: false, bulk: "" });
  const [camsGapSaving, setCamsGapSaving] = useState(false);
  // Unresolved-folio counts refreshed on mount / after gap-modal saves.
  // Buttons are hidden when count === 0 so operators only see them when
  // there's actual work to do.
  const [unresolvedCounts, setUnresolvedCounts] = useState({ cams: null, karvy: null });
  // Which card started the current handleProcess run. Controls where the
  // amber "Uploading / Processing / Streaming" progress banner is rendered
  // so the Manual card never lights up when the Auto flow is running.
  const [flowSource, setFlowSource] = useState(null); // "auto" | "manual" | null

  // Step 1 — Expected Report watchlist + tracker (Gmail auto-fetch of CAMS reports)
  const [expectedReports, setExpectedReports] = useState([]);
  const [tracker, setTracker] = useState([]);
  const [loadingExpected, setLoadingExpected] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newReport, setNewReport] = useState({
    source: "KARVY",
    add_to_db: "MFSD205_Raw",
    reference_no: "",
    file_type: "ZIP",
    password: "Kinntegra@123",
    frequency: "one_time",
    drive_url: "",
  });

  // Format an ISO timestamp as "Last rebuilt: 27 Sep 2026, 09:12 IST" — used
  // on the KARVY / VOLT tiles under Step 2 so the operator can see when
  // the cached workbook was last refreshed.
  const formatBuiltAt = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
      });
    } catch (_e) {
      return null;
    }
  };

  // Fetch unresolved counts once on mount so the amber "Map unresolved folios"
  // buttons show up only when there's tagging work pending. The CAMS count
  // MUST filter by the current period — otherwise the pill counts folios
  // whose WBR77 rows fall outside the 2026_09 workbook, and clicking through
  // to the modal (which does filter by period) shows an empty list.
  useEffect(() => {
    const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    (async () => {
      try {
        // Learn the current period first so the CAMS count call passes it.
        let per = "";
        try {
          const ar = await axios.get(`${API}/commissions/raw/latest-period`, {
            headers: authHeader, validateStatus: () => true,
          });
          per = ar.data?.period || "";
        } catch (_e) { /* silent */ }
        const camsQs = per ? `?limit=1&period=${encodeURIComponent(per)}` : "?limit=1";
        const [cams, karvy] = await Promise.all([
          axios.get(`${API}/commissions/cams/unresolved${camsQs}`, { headers: authHeader }).catch(() => null),
          axios.get(`${API}/commissions/prepare/karvy/check-gaps`, { headers: authHeader }).catch(() => null),
        ]);
        setUnresolvedCounts({
          cams: cams?.data?.total_unresolved_folios ?? 0,
          karvy: (karvy?.data?.gaps || []).length,
        });
      } catch (_e) { /* silent — buttons stay hidden on failure */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshUnresolvedCounts = async () => {
    const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    try {
      // Same period-filtered fetch as the mount effect so the pill count
      // stays in lock-step with the modal's period-scoped result.
      const per = autoPeriod?.period || "";
      const camsQs = per ? `?limit=1&period=${encodeURIComponent(per)}` : "?limit=1";
      const [cams, karvy] = await Promise.all([
        axios.get(`${API}/commissions/cams/unresolved${camsQs}`, { headers: authHeader }).catch(() => null),
        axios.get(`${API}/commissions/prepare/karvy/check-gaps`, { headers: authHeader }).catch(() => null),
      ]);
      setUnresolvedCounts({
        cams: cams?.data?.total_unresolved_folios ?? 0,
        karvy: (karvy?.data?.gaps || []).length,
      });
    } catch (_e) { /* silent */ }
  };

  // Manually open the KARVY unresolved-folios modal on demand (like the
  // CAMS "Map unresolved folios" button). Calls /check-gaps to fetch any
  // accounts still blank after the 3-step waterfall, then reuses the
  // existing gap modal for tagging + save + rebuild.
  const openKarvyUnresolvedOnDemand = async () => {
    const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    try {
      const r = await axios.get(`${API}/commissions/prepare/karvy/check-gaps`, { headers: authHeader });
      const gaps = r.data?.gaps || [];
      // Reconcile the amber pill count with the freshly-fetched result so it
      // doesn't stay stale after a dump/preview flow silently resolves gaps.
      setUnresolvedCounts((s) => ({ ...s, karvy: gaps.length }));
      if (gaps.length === 0) {
        toast.success("✓ Every KARVY account already resolves via the 3-step waterfall (prior month → raw 19941* → PAN chain).");
        return;
      }
      await openKarvyGapModal(gaps);
    } catch (e) {
      toast.error("Failed to fetch KARVY gaps", { description: e.response?.data?.detail || e.message });
    }
  };

  // Open the KARVY sub-broker tagging modal when the backend returns
  // 409 unmapped_subbroker. Loads the associate dropdown once (cached)
  // so the operator can pick a valid sub-broker per row.
  const openKarvyGapModal = async (gaps) => {
    toast.warning(`${gaps.length} KARVY sub-broker gap(s) need tagging — opening dialog…`);
    try {
      if (karvyAssociates.length === 0) {
        const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
        const r = await axios.get(`${API}/commissions/prepare/volt/associates`, { headers: authHeader });
        setKarvyAssociates(r.data?.associates || []);
      }
    } catch (_e) { /* silent — dropdown falls back to empty */ }
    setKarvyGapModal({ open: true, gaps: gaps.map((g) => ({ ...g, sub_broker: "" })) });
  };
  const updateKarvyGap = (idx, val) => {
    setKarvyGapModal((s) => {
      const g = s.gaps.slice();
      g[idx] = { ...g[idx], sub_broker: val };
      return { ...s, gaps: g };
    });
  };
  const allKarvyGapsMapped = karvyGapModal.gaps.length > 0
    && karvyGapModal.gaps.every((g) => (g.sub_broker || "").trim());
  const karvyTaggedCount = karvyGapModal.gaps.filter((g) => (g.sub_broker || "").trim()).length;
  const applyToAllKarvyGaps = (code) => {
    if (!code) return;
    setKarvyGapModal((s) => ({
      ...s,
      gaps: s.gaps.map((g) => ((g.sub_broker || "").trim() ? g : { ...g, sub_broker: code })),
    }));
  };
  const saveKarvyGapsAndRetry = async () => {
    const tagged = karvyGapModal.gaps.filter((g) => (g.sub_broker || "").trim());
    if (tagged.length === 0) { toast.error("Tag at least one row before saving"); return; }
    setKarvySavingGaps(true);
    try {
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.post(`${API}/commissions/prepare/karvy/save-gaps`, {
        entries: tagged.map((g) => ({
          account_number: g.account_number,
          sub_broker: g.sub_broker,
          investor_name: g.investor_name,
        })),
      }, { headers: authHeader });
      const remaining = karvyGapModal.gaps.length - tagged.length;
      if (remaining > 0) {
        toast.success(`Saved ${tagged.length} sub-broker mapping(s). ${remaining} row(s) still un-tagged — modal stays open.`);
        setKarvyGapModal((s) => ({
          ...s,
          gaps: s.gaps.filter((g) => !(g.sub_broker || "").trim()),
        }));
      } else {
        toast.success(`Saved ${tagged.length} sub-broker mapping(s) — retrying build…`);
        setKarvyGapModal({ open: false, gaps: [] });
        // Sync the amber-pill count so the "Map unresolved folios" button
        // disappears once the backend agrees everything is mapped.
        await refreshUnresolvedCounts();
        await retryAfterTagging();
      }
    } catch (err) {
      toast.error(`Save failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setKarvySavingGaps(false);
    }
  };

  // ── VOLT gap modal (uses same associates dropdown as KARVY) ───────────
  const openVoltGapModal = async (gaps) => {
    toast.warning(`${gaps.length} VOLT customer(s) need IFA tagging — opening dialog…`);
    try {
      if (karvyAssociates.length === 0) {
        const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
        const r = await axios.get(`${API}/commissions/prepare/volt/associates`, { headers: authHeader });
        setKarvyAssociates(r.data?.associates || []);
      }
    } catch (_e) { /* silent */ }
    setVoltGapModal({ open: true, gaps: gaps.map((g) => ({ ...g, ifa_code: "" })) });
  };
  const updateVoltGap = (idx, val) => {
    setVoltGapModal((s) => {
      const g = s.gaps.slice();
      g[idx] = { ...g[idx], ifa_code: val };
      return { ...s, gaps: g };
    });
  };
  const allVoltGapsMapped = voltGapModal.gaps.length > 0
    && voltGapModal.gaps.every((g) => (g.ifa_code || "").trim());
  const voltTaggedCount = voltGapModal.gaps.filter((g) => (g.ifa_code || "").trim()).length;
  // Bulk-apply an associate to every row that's still blank — big time
  // saver when the whole list belongs to a single sub-broker.
  const applyToAllVoltGaps = (code) => {
    if (!code) return;
    setVoltGapModal((s) => ({
      ...s,
      gaps: s.gaps.map((g) => ((g.ifa_code || "").trim() ? g : { ...g, ifa_code: code })),
    }));
  };
  const saveVoltGapsAndRetry = async () => {
    const tagged = voltGapModal.gaps.filter((g) => (g.ifa_code || "").trim());
    if (tagged.length === 0) { toast.error("Tag at least one row before saving"); return; }
    setVoltSavingGaps(true);
    try {
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.post(`${API}/commissions/prepare/volt/save-gaps`, {
        entries: tagged.map((g) => ({
          source_id: g.source_id,
          ifa_code: g.ifa_code,
          customer_name: g.customer_name,
        })),
      }, { headers: authHeader });
      const remaining = voltGapModal.gaps.length - tagged.length;
      if (remaining > 0) {
        toast.success(`Saved ${tagged.length} IFA mapping(s). ${remaining} row(s) still un-tagged — modal stays open.`);
        // Keep only un-tagged rows so the operator can continue where
        // they left off without re-opening the whole list.
        setVoltGapModal((s) => ({
          ...s,
          gaps: s.gaps.filter((g) => !(g.ifa_code || "").trim()),
        }));
      } else {
        toast.success(`Saved ${tagged.length} IFA mapping(s) — retrying build…`);
        setVoltGapModal({ open: false, gaps: [] });
        await retryAfterTagging();
      }
    } catch (err) {
      toast.error(`Save failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setVoltSavingGaps(false);
    }
  };

  // After tagging: resume the server pipeline if it's paused, else rebuild in-browser.
  const retryAfterTagging = async () => {
    if (autopilotRef.current?.stage === "needs_attention") {
      try {
        await axios.post(`${API}/commissions/autopilot/retry`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        toast.info("Auto-process pipeline will resume within a minute.");
      } catch (e) { toast.error(e.response?.data?.detail || e.message); }
      return;
    }
    handlePrepareCommissionFiles({ skipConfirm: true });
  };

  const onAutopilotState = useCallback((s) => {
    const prev = autopilotRef.current;
    autopilotRef.current = s;
    if (prev && (prev.stage !== s.stage || JSON.stringify(prev.steps) !== JSON.stringify(s.steps))) {
      hydratePrepareStatus();
      fetchRuns();
      if (s.stage === "done" && prev.stage !== "done") toast.success("Auto-process complete — sub-broker ZIP is ready.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autopilot = useAutopilot(onAutopilotState);

  // Email deep-link: /commissions?autozip=<job_id> downloads that ZIP once.
  useEffect(() => {
    const jid = new URLSearchParams(window.location.search).get("autozip");
    if (!jid) return;
    window.history.replaceState({}, "", window.location.pathname);
    toast.info("Downloading the auto-generated commission ZIP…");
    downloadResult(jid, null, { Authorization: `Bearer ${localStorage.getItem("token")}` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAutoPeriod = async () => {
    setAutoPeriodLoading(true);
    try {
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const r = await axios.get(`${API}/commissions/raw/latest-period`, { headers: authHeader });
      setAutoPeriod(r.data || null);
    } catch (e) {
      // Silent — auto tab will just fall back to the manual period picker.
      setAutoPeriod(null);
    } finally {
      setAutoPeriodLoading(false);
    }
  };

  // Hydrate prepareStatus on mount by cross-checking raw availability
  // (from `/raw/latest-period.per_source`) against each Step 2 cache
  // endpoint. Rules — Step 3's Download ZIP gate depends on this:
  //   • no raw for source           → "skipped" (grey tile, opens gate)
  //   • raw exists + fresh cache    → "ready"   (green tile, opens gate)
  //   • raw exists + stale/no cache → "idle"    (blocks gate — user must
  //                                              click Prepare Commission
  //                                              Files first)
  // This guarantees Download ZIP stays disabled whenever Step 2 knows a
  // source still needs to be processed, no matter how many old caches
  // are lying around from previous months.
  const hydratePrepareStatus = async () => {
    const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    // 1) Learn which sources have raw data for the current period.
    let ps = {};
    try {
      const ar = await axios.get(`${API}/commissions/raw/latest-period`, {
        headers: authHeader, validateStatus: () => true,
      });
      ps = ar.data?.per_source || {};
    } catch (_e) { /* leave ps empty → every source stays idle */ }

    const rawFlagFor = { karvy: "MFSD205_Raw", volt: "VOLTMONEY_Raw", cams: "WBR77_Raw" };
    const next = { karvy: "idle", volt: "idle", cams: "idle" };
    const nextBuilt = { karvy: null, volt: null, cams: null };

    // 2) Sources with no raw for this period → skipped (grey, unblocks gate).
    for (const kind of ["karvy", "volt", "cams"]) {
      if (!ps[rawFlagFor[kind]]) next[kind] = "skipped";
    }

    // 3) For sources WITH raw, peek the cache — only mark "ready" when
    //    the cache exists and isn't stale relative to the latest raw upload.
    let hidden = false;
    const peek = async (kind) => {
      if (next[kind] === "skipped") return;
      try {
        const r = await axios.get(
          `${API}/commissions/prepare/${kind}/latest-preview`,
          { headers: authHeader, validateStatus: () => true },
        );
        if (r.status === 200 && r.data?.ready === true && !r.data?.stale) {
          next[kind] = "ready";
          if (r.data?.built_at) nextBuilt[kind] = r.data.built_at;
        }
        if (r.data?.hidden_by_new_cycle) hidden = true;
        // else stay "idle" — Prepare Commission Files must be re-run.
      } catch (_e) { /* silent, remain idle */ }
    };
    await Promise.all([peek("karvy"), peek("volt"), peek("cams")]);

    setStep2Hidden(hidden);
    setPrepareStatus(next);
    setPrepareBuiltAt(nextBuilt);
  };

  const fetchExpectedReports = async () => {
    setLoadingExpected(true);
    try {
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const [rr, tr] = await Promise.all([
        axios.get(`${API}/commissions/expected-reports`, { headers: authHeader }),
        axios.get(`${API}/commissions/expected-reports/tracker`, { headers: authHeader }),
      ]);
      setExpectedReports(rr.data?.reports || []);
      setTracker(tr.data?.entries || []);
      if (tr.data?.scanner_last_run_at !== undefined) {
        setScannerLastRunAt(tr.data.scanner_last_run_at);
      }
      if (tr.data?.scanner_interval_seconds) {
        setScannerIntervalSec(tr.data.scanner_interval_seconds);
      }
    } catch (e) {
      console.error("Failed to fetch expected reports", e);
    } finally {
      setLoadingExpected(false);
    }
  };

  const addExpectedReport = async () => {
    const isVolt = newReport.source === "VOLT";
    if (isVolt && !newReport.drive_url.trim()) {
      toast.error("Paste the Google Sheets share URL");
      return;
    }
    if (!isVolt && !newReport.reference_no.trim()) {
      toast.error("Please enter a reference number");
      return;
    }
    setAdding(true);
    try {
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.post(`${API}/commissions/expected-reports`, newReport, { headers: authHeader });
      toast.success("Report added to watchlist", {
        description: isVolt
          ? `Watching VOLT sheet → ${newReport.add_to_db}`
          : `Watching for ${newReport.reference_no} → ${newReport.add_to_db}`,
      });
      setNewReport((p) => ({ ...p, reference_no: "", drive_url: "" }));
      fetchExpectedReports();
    } catch (e) {
      toast.error("Failed to add report", { description: e.response?.data?.detail || e.message });
    } finally {
      setAdding(false);
    }
  };

  const deleteExpectedReport = async (id) => {
    try {
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.delete(`${API}/commissions/expected-reports/${id}`, { headers: authHeader });
      toast.success("Report removed from watchlist");
      fetchExpectedReports();
    } catch (e) {
      toast.error("Failed to remove report", { description: e.response?.data?.detail || e.message });
    }
  };

  const [proxyStatus, setProxyStatus] = useState(null);
  const [scannerLastRunAt, setScannerLastRunAt] = useState(null);
  const [scannerIntervalSec, setScannerIntervalSec] = useState(1800);

  // "next auto-scan in ~N minutes" — derived from the scanner's last-run
  // timestamp + interval. Falls back to null (badge shows just the cadence)
  // before the first tracker fetch completes.
  const nextAutoScanMinutes = useMemo(() => {
    if (!scannerLastRunAt) return null;
    const nextMs = new Date(scannerLastRunAt).getTime() + scannerIntervalSec * 1000;
    const diffMin = Math.max(0, Math.round((nextMs - Date.now()) / 60000));
    return diffMin;
  }, [scannerLastRunAt, scannerIntervalSec]);

  const fetchProxyStatus = async () => {
    try {
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const r = await axios.get(`${API}/commissions/expected-reports/proxy-status`, { headers: authHeader });
      setProxyStatus(r.data);
    } catch (e) { /* ignore */ }
  };

  const scanExpectedReports = async () => {
    setScanning(true);
    try {
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const r = await axios.post(`${API}/commissions/expected-reports/scan`, {}, { headers: authHeader });
      toast.success("Email scan started", {
        description: `Scanning ${r.data?.checked || 0} reference(s) in background. Tracker will update automatically.`,
      });
      let poll = 0;
      const iv = setInterval(() => {
        fetchExpectedReports();
        poll += 1;
        if (poll >= 24) clearInterval(iv); // ~4 minutes @ 10s
      }, 10000);
    } catch (e) {
      toast.error("Failed to start scan", { description: e.response?.data?.detail || e.message });
    } finally {
      setScanning(false);
    }
  };

  // CAMS ZIP manual upload — mailback URLs are geo-restricted to India, so
  // the scanner can only mark the mail as "received"; the operator downloads
  // the ZIP themselves and uploads here to complete the pipeline.
  const [camsUpload, setCamsUpload] = useState({
    reference_no: "", password: "Kinntegra@123", file: null, busy: false,
    drive_url: "", drive_busy: false,
  });
  const submitCamsUpload = async () => {
    if (!camsUpload.reference_no.trim() || !camsUpload.file) {
      toast.error("Enter reference no and pick the CAMS ZIP");
      return;
    }
    setCamsUpload((p) => ({ ...p, busy: true }));
    try {
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const fd = new FormData();
      fd.append("reference_no", camsUpload.reference_no.trim());
      fd.append("add_to_db", "WBR77_Raw");
      fd.append("password", camsUpload.password);
      fd.append("file", camsUpload.file);
      const r = await axios.post(`${API}/commissions/expected-reports/upload-zip`, fd, {
        headers: { ...authHeader, "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      toast.success(`CAMS ZIP processed`, {
        description: `${(r.data?.records || 0).toLocaleString()} rows → ${r.data?.collection}`,
      });
      setCamsUpload({ reference_no: "", password: "Kinntegra@123", file: null, busy: false,
                      drive_url: "", drive_busy: false });
      fetchExpectedReports();
    } catch (e) {
      toast.error("CAMS upload failed", { description: e.response?.data?.detail || e.message });
      setCamsUpload((p) => ({ ...p, busy: false }));
    }
  };

  const submitCamsDriveFetch = async () => {
    if (!camsUpload.reference_no.trim() || !camsUpload.drive_url.trim()) {
      toast.error("Enter reference no and paste the Google Drive share URL");
      return;
    }
    setCamsUpload((p) => ({ ...p, drive_busy: true }));
    try {
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const r = await axios.post(`${API}/commissions/expected-reports/fetch-from-drive`, {
        reference_no: camsUpload.reference_no.trim(),
        drive_url: camsUpload.drive_url.trim(),
        password: camsUpload.password,
        add_to_db: "WBR77_Raw",
      }, { headers: authHeader, timeout: 900000 });
      toast.success(`CAMS ZIP fetched from Drive`, {
        description: `${(r.data?.records || 0).toLocaleString()} rows · ${(r.data?.size_bytes / 1024 / 1024).toFixed(1)} MB → ${r.data?.collection}`,
      });
      setCamsUpload({ reference_no: "", password: "Kinntegra@123", file: null, busy: false,
                      drive_url: "", drive_busy: false });
      fetchExpectedReports();
    } catch (e) {
      toast.error("Drive fetch failed", { description: e.response?.data?.detail || e.message });
      setCamsUpload((p) => ({ ...p, drive_busy: false }));
    }
  };

  // ── CAMS Step 2 preview cache handlers ────────────────────────────────
  // `runCamsBackfill` triggers the server to enrich every existing
  // WBR77_Raw row with the 3 computed columns (Unique_Code / Scheme_Name /
  // Scheme_Type). Idempotent — safe to run repeatedly after a fresh BSE
  // Scheme Master upload.
  // ── CAMS unresolved-folio tagging modal ────────────────────────────────
  // Opens a modal listing every folio whose PAN chain failed (folio missing
  // from IM, no PAN, PAN not in subbroker_mapping). Operator picks a
  // SubBroker per folio (or applies one to all rows in bulk) and saves.
  // Saved rows land in `cams_folio_subbroker_overrides` and take precedence
  // over the PAN chain on the next workbook build.
  const openCamsGapModal = async () => {
    const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    const period = autoPeriod?.period || "";
    setCamsGapModal((p) => ({ ...p, open: true, loading: true, rows: [], options: [], bulk: "" }));
    try {
      const qs = new URLSearchParams({ limit: "2000" });
      if (period) qs.set("period", period);
      const r = await axios.get(
        `${API}/commissions/cams/unresolved?${qs.toString()}`,
        { headers: authHeader },
      );
      const d = r.data || {};
      const rows = (d.rows || []).map((x) => ({ ...x, chosen: "", selected: false }));
      // Reconcile the amber pill count with the freshly-fetched result so it
      // doesn't stay stale after a preview/dump silently resolves folios.
      setUnresolvedCounts((s) => ({ ...s, cams: d.total_unresolved_folios ?? rows.length }));
      setCamsGapModal({
        open: true, loading: false, rows,
        options: d.sub_broker_options || [], bulk: "",
      });
    } catch (e) {
      setCamsGapModal((p) => ({ ...p, loading: false }));
      toast.error("Failed to load unresolved folios", {
        description: e.response?.data?.detail || e.message,
      });
    }
  };

  const applyCamsGapBulk = () => {
    setCamsGapModal((p) => {
      if (!p.bulk) return p;
      const anySelected = p.rows.some((r) => r.selected);
      // Apply to SELECTED rows. If nothing is selected, fall through to
      // still-blank rows so a user who just picks a SubBroker without
      // ticking anything gets a helpful default.
      return {
        ...p,
        rows: p.rows.map((r) =>
          anySelected
            ? (r.selected ? { ...r, chosen: p.bulk } : r)
            : (r.chosen ? r : { ...r, chosen: p.bulk })
        ),
      };
    });
  };

  const toggleCamsGapSelected = (folio) => {
    setCamsGapModal((p) => ({
      ...p,
      rows: p.rows.map((r) => r.folio_no === folio ? { ...r, selected: !r.selected } : r),
    }));
  };

  const toggleAllCamsGapSelected = (nextChecked) => {
    setCamsGapModal((p) => ({
      ...p,
      rows: p.rows.map((r) => ({ ...r, selected: !!nextChecked })),
    }));
  };

  const setCamsGapRow = (folio, code) => {
    setCamsGapModal((p) => ({
      ...p,
      rows: p.rows.map((r) => r.folio_no === folio ? { ...r, chosen: code } : r),
    }));
  };

  const saveCamsGapMappings = async () => {
    const filled = camsGapModal.rows.filter((r) => r.chosen);
    if (!filled.length) {
      toast.error("Pick a SubBroker for at least one row");
      return;
    }
    const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    const period = autoPeriod?.period || "";
    setCamsGapSaving(true);
    try {
      const r = await axios.post(
        `${API}/commissions/cams/save-folio-mappings`,
        {
          period,
          mappings: filled.map((x) => ({ folio_no: x.folio_no, sub_broker_code: x.chosen })),
        },
        { headers: authHeader },
      );
      const d = r.data || {};
      toast.success(`Saved ${d.accepted || 0} folio overrides`, {
        description: (d.rejected?.length ? `${d.rejected.length} rejected` : "") + " · cache invalidated, rebuild on next download",
      });
      setCamsGapModal({ open: false, rows: [], options: [], loading: false, bulk: "" });
      // Re-fetch the amber-pill counts from the backend so the "Map
      // unresolved folios" button disappears once every folio is tagged.
      await refreshUnresolvedCounts();
    } catch (e) {
      toast.error("Save failed", {
        description: e.response?.data?.detail || e.message,
      });
    } finally {
      setCamsGapSaving(false);
    }
  };

  // ── CAMS enriched Excel download ──────────────────────────────────────
  // Kicks off a background xlsx build (WBR77_Raw is ~200 K rows and takes
  // ~65 s to serialise — well over Cloudflare's 60 s origin-response
  // timeout). Polls status every 2 s and then hands off to the browser
  // via `window.location.assign(?token=...)` once the file is cached in
  // GridFS. Mirrors the KARVY preview flow.
  // Direct download for CAMS — mirrors handleDownloadKarvy. Cache-first
  // via `/prepare/cams/latest-preview`; falls back to a fresh
  // `/cams/preview/download/start` → poll → stream when the cache is
  // empty or stale relative to the latest WBR77_Raw upload.
  const handleDownloadCams = async () => {
    setCamsDownloading(true);
    setCamsDownloadMsg("Checking cache…");
    try {
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const bearer = localStorage.getItem("token");
      const apiBase = process.env.REACT_APP_BACKEND_URL;

      // 1) Cache check — fast path if a fresh preview already exists.
      const lpRes = await fetch(`${apiBase}/api/commissions/prepare/cams/latest-preview`, { headers: authHeader });
      const lp = lpRes.ok ? await lpRes.json() : { ready: false };
      let jobId = (lp.ready && !lp.stale && lp.job_id) ? lp.job_id : null;

      // 2) Cache miss / stale → build fresh.
      if (!jobId) {
        setCamsDownloadMsg("Building…");
        const period = autoPeriod?.period || "";
        const qs = period ? `?period=${encodeURIComponent(period)}` : "";
        const startResp = await fetch(`${apiBase}/api/commissions/cams/preview/download/start${qs}`, {
          method: "POST", headers: { Authorization: `Bearer ${bearer}` },
        });
        if (!startResp.ok) throw new Error(`start HTTP ${startResp.status}`);
        ({ job_id: jobId } = await startResp.json());
        const t0 = Date.now();
        // eslint-disable-next-line no-constant-condition
        while (true) {
          await new Promise((r) => setTimeout(r, 2000));
          const s = await fetch(`${apiBase}/api/commissions/cams/preview/download/status/${jobId}`, {
            headers: { Authorization: `Bearer ${bearer}` },
          });
          if (!s.ok) throw new Error(`status HTTP ${s.status}`);
          const st = await s.json();
          const elapsed = Math.floor((Date.now() - t0) / 1000);
          setCamsDownloadMsg(`${st.message || "Building…"} · ${elapsed}s`);
          if (st.status === "ready") break;
          if (st.status === "error") throw new Error(st.error || "build failed");
          if (elapsed > 480) throw new Error("build timed out");
        }
      }

      // 3) Stream the cached / newly-built file.
      setCamsDownloadMsg("Downloading…");
      const dt = await fetch(`${apiBase}/api/database/download-token`, { headers: authHeader });
      if (!dt.ok) throw new Error("download-token failed");
      const { token: dlToken } = await dt.json();
      window.location.assign(`${apiBase}/api/commissions/cams/preview/download/file/${jobId}?token=${dlToken}`);
      toast.success("CAMS download started");
    } catch (err) {
      toast.error(`CAMS download failed: ${err.message}`);
    } finally {
      setTimeout(() => { setCamsDownloading(false); setCamsDownloadMsg(""); }, 1500);
    }
  };

  const downloadCamsPreviewExcel = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("You need to log in again");
      return;
    }
    const authHeader = { Authorization: `Bearer ${token}` };
    const period = autoPeriod?.period || "";
    const qs = new URLSearchParams();
    if (period) qs.set("period", period);
    const startUrl = `${API}/commissions/cams/preview/download/start${qs.toString() ? `?${qs}` : ""}`;
    toast.info("Preparing enriched WBR77 workbook…", {
      description: "Building in the background (~30 s – 2 min on cold start).",
    });
    try {
      const s = await axios.post(startUrl, {}, { headers: authHeader });
      const jobId = s.data?.job_id;
      const cached = s.data?.status === "ready" || s.data?.cached;
      if (!jobId) throw new Error("no job_id returned");
      // Cache hit → skip polling entirely and go straight to file download.
      if (cached) {
        const dt = await axios.get(`${API}/database/download-token`, { headers: authHeader });
        const dlToken = dt.data?.token || token;
        window.location.assign(
          `${API}/commissions/cams/preview/download/file/${jobId}?token=${encodeURIComponent(dlToken)}`,
        );
        toast.success("Cached CAMS workbook ready");
        return;
      }
      const started = Date.now();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        const st = await axios.get(
          `${API}/commissions/cams/preview/download/status/${jobId}`,
          { headers: authHeader, validateStatus: () => true },
        );
        const data = st.data || {};
        if (data.status === "ready") {
          const dt = await axios.get(`${API}/database/download-token`, { headers: authHeader });
          const dlToken = dt.data?.token || token;
          window.location.assign(
            `${API}/commissions/cams/preview/download/file/${jobId}?token=${encodeURIComponent(dlToken)}`,
          );
          toast.success("CAMS workbook ready", {
            description: `${(data.row_count || 0).toLocaleString()} rows · ${((data.size || 0) / 1024 / 1024).toFixed(1)} MB`,
          });
          return;
        }
        if (data.status === "error") {
          throw new Error(data.error || "build failed");
        }
        const elapsed = Math.floor((Date.now() - started) / 1000);
        if (elapsed > 480) throw new Error("timed out after 8 minutes");
      }
    } catch (e) {
      toast.error("CAMS download failed", {
        description: e.response?.data?.detail || e.message,
      });
    }
  };

  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;
    fetchRuns();
    fetchExpectedReports();
    fetchProxyStatus();
    hydratePrepareStatus();
    // Resume any in-flight or completed-but-not-downloaded job left over from
    // a previous visit (e.g., user navigated away mid-processing).
    const resumeId = localStorage.getItem(LS_KEY);
    if (resumeId) resumeJob(resumeId);
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const resumeJob = async (jobId) => {
    // Non-auto-resume: on page refresh we no longer re-attach to a
    // running / completed job automatically. The old flow silently
    // reopened the download dialog which surprised operators and
    // sometimes locked the UI into "Processing…" state again.
    // New behaviour — surface a toast with the state and defer to
    // Recent Runs / the Force Stop button for user-driven recovery.
    const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    try {
      const r = await axios.get(`${API}/commissions/job-status/${jobId}`, { headers: authHeader });
      const s = r.data || {};
      if (s.state === "running" || s.state === "queued") {
        toast.info("A previous commission job is still running. It will show in Recent Runs when done — refresh the page later to grab the ZIP.");
      } else if (s.state === "completed") {
        toast.info("Your last ZIP is ready in Recent Runs below.");
        localStorage.removeItem(LS_KEY);
      } else if (s.state === "failed") {
        toast.error(`Previous job failed: ${s.error || "unknown error"}`);
        localStorage.removeItem(LS_KEY);
      }
      fetchRuns();
    } catch (e) {
      // 404 means the job dir was cleaned up; clear the stale id
      if (e.response?.status === 404) localStorage.removeItem(LS_KEY);
    }
  };

  const downloadResult = async (jobId, status, authHeader) => {
    setStage("downloading");
    try {
      // Native `fetch` instead of axios for the actual blob transfer —
      // it streams the response chunks directly into the browser's blob
      // buffer instead of axios' in-memory accumulator. That avoids
      // the silent stall some users were seeing on 60+ MB ZIPs behind
      // the K8s ingress when the connection took >60s on slower links.
      const r = await fetch(`${API}/commissions/job-result/${jobId}`, {
        method: "GET",
        headers: authHeader,
        // No timeout — large ZIPs on slow connections can legitimately
        // need several minutes.
      });
      if (!r.ok) {
        let detail = `HTTP ${r.status}`;
        try {
          const j = await r.json();
          detail = j.detail || detail;
        } catch (_) {
          /* response wasn't JSON */
        }
        throw new Error(detail);
      }
      const blob = await r.blob();
      const filename =
        (status && status.filename) ||
        `commissions_${period || (status && status.period) || jobId}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Keep the URL alive briefly so the browser has time to start the
      // download before we revoke; revoking too early occasionally causes
      // Firefox / Safari to abort the save dialog.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success(`Downloaded ${filename}`);
      localStorage.removeItem(LS_KEY);
      fetchRuns();
    } catch (e) {
      toast.error(`Download failed: ${e.message || e}`);
    } finally {
      setStage("idle");
    }
  };

  const handleDownloadExisting = async (jobId) => {
    const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    try {
      const r = await axios.get(`${API}/commissions/job-status/${jobId}`, { headers: authHeader });
      await downloadResult(jobId, r.data, authHeader);
    } catch (e) {
      toast.error(`Cannot fetch this run: ${e.response?.data?.detail || e.message}`);
      fetchRuns();
    }
  };

  const fetchRuns = async () => {
    try {
      const r = await axios.get(`${API}/commissions/runs`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setRuns(r.data?.runs || []);
    } catch {
      /* ignore */
    }
  };

  const uploadFileChunked = async (jobId, source, file, authHeader, expectedSources, periodVal) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const expectedCsv = expectedSources.join(",");
    for (let i = 0; i < totalChunks; i += 1) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(file.size, start + CHUNK_SIZE);
      const blob = file.slice(start, end);
      const fd = new FormData();
      fd.append("job_id", jobId);
      fd.append("source", source);
      fd.append("chunk_index", String(i));
      fd.append("total_chunks", String(totalChunks));
      fd.append("filename", file.name);
      fd.append("expected_sources", expectedCsv);
      if (periodVal) fd.append("period", periodVal);
      fd.append("chunk", blob, "chunk");
      let lastErr = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          await axios.post(`${API}/commissions/upload-chunk`, fd, {
            headers: { ...authHeader, "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
        }
      }
      if (lastErr) throw lastErr;
      const pct = Math.round(((i + 1) * 100) / totalChunks);
      setProgressBySource((p) => ({ ...p, [source]: pct }));
    }
  };

  const pollUntilDone = (jobId, authHeader, resolve, reject) => {
    // Track transient errors (502/503/504/network) so the worker can
    // keep processing even when the ingress hiccups or a pod recycles
    // mid-job. The actual job runs on a background thread tied to the
    // mongo `commission_job_state` doc, NOT this HTTP connection, so
    // ingress 502s are recoverable — we just have to keep polling.
    let transientErrors = 0;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/commissions/job-status/${jobId}`, { headers: authHeader });
        const s = r.data || {};
        transientErrors = 0;  // reset on any successful poll
        setJobMessage(s.message || s.state || "");
        if (s.state === "completed") return resolve(s);
        if (s.state === "failed") return reject(new Error(s.error || "Processing failed"));
        pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        const status = e?.response?.status ?? 0;  // 0 = network / CORS / dropped
        if (POLL_TRANSIENT_STATUSES.includes(status)) {
          transientErrors += 1;
          if (transientErrors >= POLL_MAX_TRANSIENT_ERRORS) {
            return reject(new Error(
              `Lost contact with the server after ${transientErrors} retries — ` +
              `your ZIP is most likely still being built. Reload this page in a ` +
              `minute and look for it in "Recent runs".`
            ));
          }
          // Surface the retry status to the user so they don't think we're stuck
          setJobMessage(`Server hiccup (${status || "network"}) — retry ${transientErrors}/${POLL_MAX_TRANSIENT_ERRORS}…`);
          pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);
          return;
        }
        return reject(e);
      }
    };
    tick();
  };

  const handleForceKill = async () => {
    const jobId = localStorage.getItem(LS_KEY);
    if (!jobId) {
      // Nothing tracked locally — just reset the UI and clear LS as a safety net.
      localStorage.removeItem(LS_KEY);
      if (pollRef.current) clearTimeout(pollRef.current);
      setStage("idle");
      setJobMessage("");
      setProgressBySource({});
      toast.info("UI reset. No active job was tracked locally.");
      return;
    }
    const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    if (!window.confirm(
      "Force-kill the current commission job?\n\n" +
      "This marks the job as failed and clears its temp files so you can " +
      "restart from scratch. Any partially-built ZIP is discarded."
    )) return;
    try {
      const r = await axios.post(`${API}/commissions/cancel-job/${jobId}`, null, { headers: authHeader });
      const cleaned = r.data || {};
      toast.success(
        `Job force-killed (was: ${cleaned.previous_state || "unknown"}). ` +
        `Cleared ${cleaned.chunks_deleted || 0} chunks. You can restart now.`
      );
    } catch (e) {
      toast.error(`Force-kill failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      localStorage.removeItem(LS_KEY);
      if (pollRef.current) clearTimeout(pollRef.current);
      setStage("idle");
      setJobMessage("");
      setProgressBySource({});
      fetchRuns();
    }
  };

  // Prepare Commission Files — checks raw availability from Step 1 for
  // all three sources (MFSD205_Raw, VOLTMONEY_Raw, WBR77_Raw), warns
  // the operator if any is missing, then runs KARVY → VOLT → CAMS in
  // sequence with per-source live status on the tiles below.
  //
  // `opts.only` (optional) restricts the run to a single source
  // ("cams" | "karvy" | "volt"). Called by the per-tile Rebuild buttons
  // so the operator can re-run just one RTA after tagging unresolved
  // folios, without wasting time rebuilding the two that are still Ready.
  const handlePrepareCommissionFiles = async (opts = {}) => {
    const skipConfirm = opts?.skipConfirm === true;
    const only = opts?.only || null;  // 'cams' | 'karvy' | 'volt' | null (=all)
    const wantCams  = !only || only === "cams";
    const wantKarvy = !only || only === "karvy";
    const wantVolt  = !only || only === "volt";
    // 1) Availability check via autoPeriod.per_source (populated by
    //    /api/commissions/raw/latest-period). null → no raw data.
    const ps = autoPeriod?.per_source || {};
    const missing = [];
    if (wantKarvy && !ps.MFSD205_Raw) missing.push("KARVY (MFSD205_Raw)");
    if (wantVolt  && !ps.VOLTMONEY_Raw) missing.push("VOLT (VOLTMONEY_Raw)");
    if (wantCams  && !ps.WBR77_Raw) missing.push("CAMS (WBR77_Raw)");
    if (missing.length > 0 && !skipConfirm) {
      const proceed = window.confirm(
        `Step 1 hasn't delivered raw data for:\n\n  • ${missing.join("\n  • ")}\n\n` +
        `Do you want to proceed and prepare only the sources that DO have raw data?\n\n` +
        `Click OK to continue with the available sources.\n` +
        `Click Cancel to wait for the next auto-scan.`
      );
      if (!proceed) return;
    }

    setPrepareBusy(true);
    setPrepareMsg("Starting…");
    // Fresh AbortController for this run — Force Stop calls .abort().
    const abortCtrl = new AbortController();
    prepareAbortRef.current = abortCtrl;
    prepareJobIdRef.current = null;
    const nextStatus = { karvy: "idle", volt: "idle", cams: "idle" };
    // When targeting a single source, leave the other two tiles untouched
    // (preserves their existing "Ready" / "Last rebuilt" state).
    if (only) {
      nextStatus.karvy = prepareStatus.karvy;
      nextStatus.volt  = prepareStatus.volt;
      nextStatus.cams  = prepareStatus.cams;
    }
    // Mark unavailable sources as skipped up-front so the tiles reflect
    // "no raw data" without a build attempt.
    if (wantKarvy && !ps.MFSD205_Raw) nextStatus.karvy = "skipped";
    if (wantVolt  && !ps.VOLTMONEY_Raw) nextStatus.volt = "skipped";
    if (wantCams  && !ps.WBR77_Raw) nextStatus.cams = "skipped";
    setPrepareStatus({ ...nextStatus });
    // Reset progress bars for the source(s) being rebuilt only.
    setPrepareProgress((p) => ({
      karvy: wantKarvy ? { percent: 0, message: "", elapsed: 0 } : p.karvy,
      volt:  wantVolt  ? { percent: 0, message: "", elapsed: 0 } : p.volt,
      cams:  wantCams  ? { percent: 0, message: "", elapsed: 0 } : p.cams,
    }));
    // Clear stale "last rebuilt" timestamps only for the source(s) about
    // to rebuild — untouched tiles keep their existing timestamps.
    setPrepareBuiltAt((p) => ({
      karvy: wantKarvy ? null : p.karvy,
      volt:  wantVolt  ? null : p.volt,
      cams:  wantCams  ? null : p.cams,
    }));

    try {
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const bearer = localStorage.getItem("token");
      const apiBase = process.env.REACT_APP_BACKEND_URL;

      // ── CAMS ─────────────────────────────────────────────────────
      // Runs first (matches the tile order CAMS → KARVY → VOLT). Mirrors
      // the KARVY flow: kick /cams/preview/download/start, poll /status
      // until ready, refresh the "last rebuilt" timestamp. Wrapped in its
      // own try so a CAMS failure (520 / timeout) doesn't abort KARVY / VOLT.
      if (wantCams && ps.WBR77_Raw) {
        try {
          setPrepareStatus((p) => ({ ...p, cams: "running" }));
          setPrepareMsg("CAMS: building workbook…");
          const camsPeriod = autoPeriod?.period || "";
          const camsQs = camsPeriod ? `?period=${encodeURIComponent(camsPeriod)}` : "";
          const camsStart = await fetch(`${apiBase}/api/commissions/cams/preview/download/start${camsQs}`, {
            method: "POST", headers: { Authorization: `Bearer ${bearer}` },
            signal: abortCtrl.signal,
          });
          if (!camsStart.ok) {
            const t = await camsStart.text();
            toast.warning(`CAMS skipped — start HTTP ${camsStart.status} ${t.slice(0, 80)}`);
            setPrepareStatus((p) => ({ ...p, cams: "error" }));
          } else {
            const { job_id: camsJobId } = await camsStart.json();
            prepareJobIdRef.current = camsJobId;
            const camsT0 = Date.now();
            // eslint-disable-next-line no-constant-condition
            while (true) {
              if (abortCtrl.signal.aborted) throw new DOMException("aborted", "AbortError");
              await new Promise((r) => setTimeout(r, 2000));
              if (abortCtrl.signal.aborted) throw new DOMException("aborted", "AbortError");
              const s = await fetch(`${apiBase}/api/commissions/cams/preview/download/status/${camsJobId}`, {
                headers: { Authorization: `Bearer ${bearer}` },
                signal: abortCtrl.signal,
              });
              if (!s.ok) throw new Error(`CAMS status HTTP ${s.status}`);
              const st = await s.json();
              const elapsed = Math.floor((Date.now() - camsT0) / 1000);
              setPrepareMsg(`CAMS: ${st.message || "Building…"} · ${elapsed}s`);
              setPrepareProgress((p) => ({
                ...p,
                cams: {
                  percent: typeof st.percent === "number" ? st.percent : p.cams.percent,
                  message: st.message || "Building…",
                  elapsed,
                },
              }));
              if (st.status === "ready") break;
              if (st.status === "error") throw new Error(st.error || "CAMS build failed");
              if (elapsed > 420) throw new Error("CAMS build timed out");
            }
            prepareJobIdRef.current = null;
            setPrepareStatus((p) => ({ ...p, cams: "ready" }));
            setPrepareProgress((p) => ({ ...p, cams: { percent: 100, message: "Ready", elapsed: p.cams.elapsed } }));
            try {
              const lp = await axios.get(`${API}/commissions/prepare/cams/latest-preview`, { headers: authHeader });
              if (lp.data?.built_at) setPrepareBuiltAt((p) => ({ ...p, cams: lp.data.built_at }));
            } catch (_e) { /* silent */ }
          }
        } catch (cerr) {
          if (cerr.name === "CanceledError" || cerr.name === "AbortError") throw cerr;
          // 520 / network / timeout / build error — mark CAMS as failed
          // and keep going with KARVY / VOLT so the operator doesn't have
          // to re-run the whole pipeline.
          toast.error(`CAMS failed — ${cerr.message}. Continuing with KARVY / VOLT.`);
          setPrepareStatus((p) => ({ ...p, cams: "error" }));
          prepareJobIdRef.current = null;
        }
      }

      // ── KARVY ────────────────────────────────────────────────────
      if (wantKarvy && ps.MFSD205_Raw) {
        setPrepareStatus((p) => ({ ...p, karvy: "running" }));
        setPrepareMsg("KARVY: dumping raw → MFSD205…");
        try {
          await axios.post(`${API}/commissions/prepare/karvy/dump-to-mfsd205`, {}, { headers: authHeader, signal: abortCtrl.signal });
        } catch (err) {
          if (err.name === "CanceledError" || err.name === "AbortError") throw err;
          const detail = err.response?.data?.detail;
          if (err.response?.status === 409 && detail?.error === "unmapped_subbroker") {
            setPrepareStatus((p) => ({ ...p, karvy: "idle" }));
            setPrepareBusy(false);
            setPrepareMsg("");
            await openKarvyGapModal(detail.gaps || []);
            return;
          }
        }
        setPrepareMsg("KARVY: building workbook…");
        const startResp = await fetch(`${apiBase}/api/commissions/prepare/karvy/preview-download/start`, {
          method: "POST", headers: { Authorization: `Bearer ${bearer}` },
          signal: abortCtrl.signal,
        });
        if (startResp.status === 409) {
          const j = await startResp.json();
          if (j?.detail?.error === "unmapped_subbroker") {
            setPrepareStatus((p) => ({ ...p, karvy: "idle" }));
            setPrepareBusy(false);
            setPrepareMsg("");
            await openKarvyGapModal(j.detail.gaps || []);
            return;
          }
          const err = j?.detail?.error || "gap";
          toast.error(`${(j?.detail?.gaps || []).length} ${err === "unmapped_subbroker" ? "sub-broker" : "brokerage-type"} gap(s) — resolve from Downloads.`);
          setPrepareStatus((p) => ({ ...p, karvy: "error" }));
          return;
        }
        if (!startResp.ok) throw new Error(`start HTTP ${startResp.status}`);
        const { job_id: jobId } = await startResp.json();
        // Remember the job id so Force Stop can also cancel it server-side.
        prepareJobIdRef.current = jobId;
        const t0 = Date.now();
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (abortCtrl.signal.aborted) throw new DOMException("aborted", "AbortError");
          await new Promise((r) => setTimeout(r, 2000));
          if (abortCtrl.signal.aborted) throw new DOMException("aborted", "AbortError");
          const s = await fetch(`${apiBase}/api/commissions/prepare/karvy/preview-download/status/${jobId}`, {
            headers: { Authorization: `Bearer ${bearer}` },
            signal: abortCtrl.signal,
          });
          if (!s.ok) throw new Error(`status HTTP ${s.status}`);
          const st = await s.json();
          const elapsed = Math.floor((Date.now() - t0) / 1000);
          setPrepareMsg(`KARVY: ${st.message || "Building…"} · ${elapsed}s`);
          setPrepareProgress((p) => ({
            ...p,
            karvy: {
              percent: typeof st.percent === "number" ? st.percent : p.karvy.percent,
              message: st.message || "Building…",
              elapsed,
            },
          }));
          if (st.status === "ready") break;
          if (st.status === "error") throw new Error(st.error || "KARVY build failed");
          if (elapsed > 420) throw new Error("KARVY build timed out");
        }
        prepareJobIdRef.current = null;
        setPrepareStatus((p) => ({ ...p, karvy: "ready" }));
        setPrepareProgress((p) => ({ ...p, karvy: { percent: 100, message: "Ready", elapsed: p.karvy.elapsed } }));
        // Refresh "last rebuilt" from the cache metadata endpoint.
        try {
          const lp = await axios.get(`${API}/commissions/prepare/karvy/latest-preview`, { headers: authHeader });
          if (lp.data?.built_at) setPrepareBuiltAt((p) => ({ ...p, karvy: lp.data.built_at }));
        } catch (_e) { /* silent */ }
      }

      // ── VOLT ─────────────────────────────────────────────────────
      if (wantVolt && ps.VOLTMONEY_Raw) {
        setPrepareStatus((p) => ({ ...p, volt: "running" }));
        setPrepareMsg("VOLT: building workbook…");
        // VOLT is a single blocking request (no status polling endpoint).
        // Show an animated pseudo-progress so the tile isn't stuck at 0%.
        const voltT0 = Date.now();
        setPrepareProgress((p) => ({ ...p, volt: { percent: 5, message: "Building workbook…", elapsed: 0 } }));
        const voltTick = setInterval(() => {
          const el = Math.floor((Date.now() - voltT0) / 1000);
          setPrepareProgress((p) => ({
            ...p,
            volt: {
              // Ease toward 90% over ~30s; final 10% arrives when the
              // response lands and we flip to "ready".
              percent: Math.min(90, 5 + Math.floor(el * 3)),
              message: "Building workbook…",
              elapsed: el,
            },
          }));
        }, 1000);
        try {
          await axios.get(`${API}/commissions/prepare/volt`, {
            responseType: "blob", headers: authHeader, signal: abortCtrl.signal,
          });
          clearInterval(voltTick);
          setPrepareStatus((p) => ({ ...p, volt: "ready" }));
          setPrepareProgress((p) => ({ ...p, volt: { percent: 100, message: "Ready", elapsed: p.volt.elapsed } }));
          try {
            const lp = await axios.get(`${API}/commissions/prepare/volt/latest-preview`, { headers: authHeader });
            if (lp.data?.built_at) setPrepareBuiltAt((p) => ({ ...p, volt: lp.data.built_at }));
          } catch (_e) { /* silent */ }
        } catch (verr) {
          clearInterval(voltTick);
          if (verr.name === "CanceledError" || verr.name === "AbortError") throw verr;
          // axios was configured with responseType:"blob", so on a JSON 409
          // response.data comes back as a Blob. Parse it into JSON before
          // checking the error code — otherwise `unmapped_ifa` is never
          // detected and the VOLT modal never opens.
          let vdetail = verr.response?.data;
          if (vdetail && typeof vdetail.text === "function") {
            try { vdetail = JSON.parse(await vdetail.text())?.detail; }
            catch (_pe) { vdetail = null; }
          } else if (vdetail && typeof vdetail === "object" && "detail" in vdetail) {
            vdetail = vdetail.detail;
          }
          if (verr.response?.status === 409 && vdetail?.error === "unmapped_ifa") {
            setPrepareStatus((p) => ({ ...p, volt: "idle" }));
            setPrepareBusy(false);
            setPrepareMsg("");
            await openVoltGapModal(vdetail.gaps || []);
            return;
          }
          toast.warning(`VOLT skipped — ${vdetail?.error || verr.message}`);
          setPrepareStatus((p) => ({ ...p, volt: "error" }));
        }
      }

      toast.success(only
        ? `${only.toUpperCase()} commission file rebuilt. Use Step 3 to generate the sub-broker ZIP.`
        : "Commission files prepared. Use Step 3 to generate the sub-broker ZIP.");
    } catch (err) {
      if (err.name === "CanceledError" || err.name === "AbortError") {
        toast.info("Prepare Commission Files stopped by user.");
        setPrepareStatus((p) => ({
          karvy: p.karvy === "running" ? "error" : p.karvy,
          volt:  p.volt  === "running" ? "error" : p.volt,
          cams:  p.cams  === "running" ? "error" : p.cams,
        }));
      } else {
        // Any other error (network / 520 / timeout) must NOT leave a tile
        // stuck on "Building…" — flip every in-flight tile to "error" so
        // the operator sees a Failed badge and can re-run.
        toast.error(`Prepare failed: ${err.message}`);
        setPrepareStatus((p) => ({
          karvy: p.karvy === "running" ? "error" : p.karvy,
          volt:  p.volt  === "running" ? "error" : p.volt,
          cams:  p.cams  === "running" ? "error" : p.cams,
        }));
      }
    } finally {
      prepareAbortRef.current = null;
      prepareJobIdRef.current = null;
      setPrepareBusy(false);
      setPrepareMsg("");
    }
  };

  // Force-stop the in-flight Prepare Commission Files run. Cancels every
  // pending HTTP call via AbortController AND asks the server to nuke the
  // KARVY GridFS job so its worker doesn't keep grinding after the user
  // navigates away.
  const handleForceStopPrepare = async () => {
    if (!prepareBusy) return;
    if (!window.confirm(
      "Force-stop the current Prepare Commission Files run?\n\n" +
      "In-flight KARVY / VOLT builds will be cancelled and any partial " +
      "cache is discarded. You can restart from scratch afterwards."
    )) return;
    try {
      if (prepareAbortRef.current) prepareAbortRef.current.abort();
      const jobId = prepareJobIdRef.current;
      if (jobId) {
        const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
        try {
          await axios.post(`${API}/commissions/cancel-job/${jobId}`, null, { headers: authHeader });
        } catch (_e) { /* server-side cleanup is best-effort */ }
      }
    } finally {
      prepareAbortRef.current = null;
      prepareJobIdRef.current = null;
      setPrepareBusy(false);
      setPrepareMsg("");
      // Any tile still on "running" gets marked as error so the user can
      // see where the stop landed.
      setPrepareStatus((p) => ({
        karvy: p.karvy === "running" ? "error" : p.karvy,
        volt:  p.volt  === "running" ? "error" : p.volt,
        cams:  p.cams  === "running" ? "error" : p.cams,
      }));
      toast.info("Prepare Commission Files stopped.");
    }
  };

  // Direct download for KARVY — cache-first: streams the cached preview
  // file when it's fresh, only rebuilding if the cache is missing/stale.
  // The download-token pattern sidesteps Cloudflare's 100s timeout on
  // 40+ MB payloads.
  const handleDownloadKarvy = async () => {
    setKarvyDownloading(true);
    setKarvyDownloadMsg("Checking cache…");
    try {
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const bearer = localStorage.getItem("token");
      const apiBase = process.env.REACT_APP_BACKEND_URL;

      // 1) Cache check first — fast path if a fresh preview already exists.
      const lpRes = await fetch(`${apiBase}/api/commissions/prepare/karvy/latest-preview`, { headers: authHeader });
      const lp = lpRes.ok ? await lpRes.json() : { ready: false };
      let jobId = (lp.ready && !lp.stale && lp.job_id) ? lp.job_id : null;

      // 2) Cache miss/stale → build fresh (surfaces the tagging modal on 409).
      if (!jobId) {
        setKarvyDownloadMsg("Building…");
        try {
          await axios.post(`${API}/commissions/prepare/karvy/dump-to-mfsd205`, {}, { headers: authHeader });
        } catch (err) {
          const detail = err.response?.data?.detail;
          if (err.response?.status === 409 && detail?.error === "unmapped_subbroker") {
            toast.error(`${(detail.gaps || []).length} KARVY account(s) need tagging — resolve from Downloads.`);
            setKarvyDownloading(false); setKarvyDownloadMsg("");
            return;
          }
        }
        const startResp = await fetch(`${apiBase}/api/commissions/prepare/karvy/preview-download/start`, {
          method: "POST", headers: { Authorization: `Bearer ${bearer}` },
        });
        if (startResp.status === 409) {
          const j = await startResp.json();
          const err = j?.detail?.error || "gap";
          toast.error(`${(j?.detail?.gaps || []).length} ${err === "unmapped_subbroker" ? "sub-broker" : "brokerage-type"} gap(s) — resolve from Downloads.`);
          setKarvyDownloading(false); setKarvyDownloadMsg("");
          return;
        }
        if (!startResp.ok) throw new Error(`start HTTP ${startResp.status}`);
        ({ job_id: jobId } = await startResp.json());
        const t0 = Date.now();
        // eslint-disable-next-line no-constant-condition
        while (true) {
          await new Promise((r) => setTimeout(r, 2000));
          const s = await fetch(`${apiBase}/api/commissions/prepare/karvy/preview-download/status/${jobId}`, {
            headers: { Authorization: `Bearer ${bearer}` },
          });
          if (!s.ok) throw new Error(`status HTTP ${s.status}`);
          const st = await s.json();
          const elapsed = Math.floor((Date.now() - t0) / 1000);
          setKarvyDownloadMsg(`${st.message || "Building…"} · ${elapsed}s`);
          if (st.status === "ready") break;
          if (st.status === "error") throw new Error(st.error || "build failed");
          if (elapsed > 420) throw new Error("build timed out");
        }
      }

      // 3) Stream the cached / newly-built file.
      setKarvyDownloadMsg("Downloading…");
      const dt = await fetch(`${apiBase}/api/database/download-token`, { headers: authHeader });
      if (!dt.ok) throw new Error("download-token failed");
      const { token: dlToken } = await dt.json();
      window.location.assign(`${apiBase}/api/commissions/prepare/karvy/preview-download/file/${jobId}?token=${dlToken}`);
      toast.success("KARVY download started");
    } catch (err) {
      toast.error(`KARVY download failed: ${err.message}`);
    } finally {
      setTimeout(() => { setKarvyDownloading(false); setKarvyDownloadMsg(""); }, 1500);
    }
  };

  // Direct download for VOLT — cache-first via `volt_preview_cache`.
  // Falls back to a synchronous /prepare/volt rebuild if the cache is
  // empty or stale relative to the latest VOLTMONEY_Raw upload.
  const handleDownloadVolt = async () => {
    setVoltDownloading(true);
    setVoltDownloadMsg("Checking cache…");
    try {
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const apiBase = process.env.REACT_APP_BACKEND_URL;

      // 1) Cache check — fast path via the cached GridFS blob.
      const lpRes = await fetch(`${apiBase}/api/commissions/prepare/volt/latest-preview`, { headers: authHeader });
      const lp = lpRes.ok ? await lpRes.json() : { ready: false };
      if (lp.ready && !lp.stale) {
        setVoltDownloadMsg("Downloading…");
        const dt = await fetch(`${apiBase}/api/database/download-token`, { headers: authHeader });
        const { token: dlToken } = dt.ok ? await dt.json() : { token: null };
        window.location.assign(`${apiBase}/api/commissions/prepare/volt/latest-preview/file?token=${dlToken || ""}`);
        toast.success("VOLT download started");
        return;
      }

      // 2) Cache miss/stale → sync build (fast for a few-hundred-row workbook).
      setVoltDownloadMsg("Building…");
      const resp = await axios.get(`${API}/commissions/prepare/volt`, {
        responseType: "blob", headers: authHeader,
      });
      const cd = resp.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      const fname = m ? m[1] : `${autoPeriod?.period || period}_VOLT.xlsx`;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`VOLT: ${resp.headers["x-volt-row-count"] || "?"} rows`);
    } catch (err) {
      let d = err.response?.data;
      if (d instanceof Blob) {
        try { d = JSON.parse(await d.text()); } catch { d = null; }
      }
      const detail = d?.detail;
      if (err.response?.status === 409 && detail?.error === "unmapped_ifa") {
        toast.error(`${(detail.gaps || []).length} VOLT customer(s) need IFA tagging — resolve from Downloads.`);
      } else {
        toast.error(`VOLT download failed: ${typeof detail === "string" ? detail : err.message}`);
      }
    } finally {
      setTimeout(() => { setVoltDownloading(false); setVoltDownloadMsg(""); }, 1000);
    }
  };

  // Auto Process & Download ZIP — mirrors the Manual card's "Process &
  // Download ZIP" button. Pulls the cached CAMS, KARVY and VOLT workbooks
  // from the raw dumps and pipes them into handleProcess so the Step 2
  // pipeline emits the same per-sub-broker password-protected ZIP as the
  // manual upload path.
  const handleAutoProcessZip = async () => {
    const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    const bearer = localStorage.getItem("token");
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    setProcessSource("auto");
    const t = toast.loading("Preparing sub-broker wise files…");
    try {
      // ── CAMS — cache-first via `/prepare/cams/latest-preview` → file. ──
      let camsFile = null;
      try {
        const camsLpRes = await fetch(`${apiBase}/api/commissions/prepare/cams/latest-preview`, { headers: authHeader });
        const camsLp = camsLpRes.ok ? await camsLpRes.json() : { ready: false };
        if (camsLp.ready && !camsLp.stale && camsLp.job_id) {
          const dtC = await fetch(`${apiBase}/api/database/download-token`, { headers: authHeader });
          if (!dtC.ok) throw new Error("CAMS download-token failed");
          const { token: camsDlToken } = await dtC.json();
          const camsFileResp = await fetch(
            `${apiBase}/api/commissions/cams/preview/download/file/${camsLp.job_id}?token=${camsDlToken}`,
          );
          if (!camsFileResp.ok) throw new Error(`CAMS file HTTP ${camsFileResp.status}`);
          const camsBlob = await camsFileResp.blob();
          camsFile = new File([camsBlob], camsLp.filename || `${autoPeriod?.period || period}_CAMS.xlsx`, {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
        }
      } catch (cerr) {
        toast.warning(`CAMS skipped — ${cerr.message}`);
      }

      // ── KARVY — cache-first. Only rebuild if the cached preview is
      // missing or stale relative to the latest MFSD205_Raw upload.
      let karvyFile = null;
      const karvyLpRes = await fetch(`${apiBase}/api/commissions/prepare/karvy/latest-preview`, { headers: authHeader });
      const karvyLp = karvyLpRes.ok ? await karvyLpRes.json() : { ready: false };
      let karvyJobId = null;
      if (karvyLp.ready && !karvyLp.stale && karvyLp.job_id) {
        karvyJobId = karvyLp.job_id;
      } else {
        // Cache miss or stale — build fresh (silent to the operator; the
        // outer toast keeps saying "Preparing sub-broker wise files…").
        try {
          await axios.post(`${API}/commissions/prepare/karvy/dump-to-mfsd205`, {}, { headers: authHeader });
        } catch (err) {
          const detail = err.response?.data?.detail;
          if (err.response?.status === 409 && detail?.error === "unmapped_subbroker") {
            toast.dismiss(t);
            toast.error(`${(detail.gaps || []).length} KARVY account(s) need tagging — use the KARVY tile above.`);
            return;
          }
          // any other dump error is non-fatal — continue to build
        }
        const startResp = await fetch(`${apiBase}/api/commissions/prepare/karvy/preview-download/start`, {
          method: "POST", headers: { Authorization: `Bearer ${bearer}` },
        });
        if (startResp.status === 409) {
          const j = await startResp.json();
          const err = j?.detail?.error || "gap";
          toast.dismiss(t);
          toast.error(`${(j?.detail?.gaps || []).length} ${err === "unmapped_subbroker" ? "sub-broker" : "brokerage-type"} gap(s) — use the KARVY tile.`);
          return;
        }
        if (!startResp.ok) throw new Error(`KARVY start HTTP ${startResp.status}`);
        ({ job_id: karvyJobId } = await startResp.json());
        const t0 = Date.now();
        // eslint-disable-next-line no-constant-condition
        while (true) {
          await new Promise((r) => setTimeout(r, 2000));
          const s = await fetch(`${apiBase}/api/commissions/prepare/karvy/preview-download/status/${karvyJobId}`, {
            headers: { Authorization: `Bearer ${bearer}` },
          });
          if (!s.ok) throw new Error(`KARVY status HTTP ${s.status}`);
          const st = await s.json();
          if (st.status === "ready") break;
          if (st.status === "error") throw new Error(st.error || "KARVY build failed");
          if (Math.floor((Date.now() - t0) / 1000) > 420) throw new Error("KARVY build timed out");
        }
      }
      // Stream the cached / just-built KARVY workbook via download-token.
      const dtResp = await fetch(`${apiBase}/api/database/download-token`, { headers: authHeader });
      if (!dtResp.ok) throw new Error("KARVY download-token failed");
      const { token: karvyDlToken } = await dtResp.json();
      const karvyFileResp = await fetch(`${apiBase}/api/commissions/prepare/karvy/preview-download/file/${karvyJobId}?token=${karvyDlToken}`);
      if (!karvyFileResp.ok) throw new Error(`KARVY file HTTP ${karvyFileResp.status}`);
      const karvyBlob = await karvyFileResp.blob();
      const karvyName = karvyLp.filename || `${autoPeriod?.period || period}_KARVY.xlsx`;
      karvyFile = new File([karvyBlob], karvyName, {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // VOLT — cache-first via `volt_preview_cache`, fallback to sync build.
      let voltFile = null;
      try {
        const voltLpRes = await fetch(`${apiBase}/api/commissions/prepare/volt/latest-preview`, { headers: authHeader });
        const voltLp = voltLpRes.ok ? await voltLpRes.json() : { ready: false };
        if (voltLp.ready && !voltLp.stale) {
          const dt2 = await fetch(`${apiBase}/api/database/download-token`, { headers: authHeader });
          const { token: voltDlToken } = dt2.ok ? await dt2.json() : { token: null };
          const voltFileResp = await fetch(`${apiBase}/api/commissions/prepare/volt/latest-preview/file?token=${voltDlToken}`, { headers: authHeader });
          if (voltFileResp.ok) {
            const voltBlob = await voltFileResp.blob();
            voltFile = new File([voltBlob], voltLp.filename || `${autoPeriod?.period || period}_VOLT.xlsx`, {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
          }
        }
        if (!voltFile) {
          const voltResp = await axios.get(`${API}/commissions/prepare/volt`, {
            responseType: "blob", headers: authHeader,
          });
          const cd = voltResp.headers["content-disposition"] || "";
          const mMatch = /filename="?([^"]+)"?/i.exec(cd);
          const voltName = mMatch ? mMatch[1] : `${autoPeriod?.period || period}_VOLT.xlsx`;
          voltFile = new File([voltResp.data], voltName, {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
        }
      } catch (verr) {
        const vdetail = verr.response?.data;
        toast.warning(`VOLT skipped — ${vdetail?.detail?.error || verr.message}`);
      }

      toast.dismiss(t);
      // Hand off to Step 2 pipeline (chunked upload → server splits per
      // sub-broker → password-protected ZIP). `forceDownload:true` skips
      // the auto-chained silent path so the browser gets the ZIP directly.
      // CAMS, KARVY and VOLT all come from cached previews — any missing
      // source (e.g. skipped because no raw for this period) is passed as
      // null so handleProcess's partial-attach guard is honoured.
      await handleProcess({ camsFile, karvyFile, voltFile, forceDownload: true });
    } catch (err) {
      toast.dismiss(t);
      toast.error(`Auto-process failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleProcess = async (overrides = {}) => {
    // overrides: { camsFile, karvyFile, voltFile, forceDownload } — bypass
    // state for the auto-chained VOLT flow that hands us the prepared blob
    // before the React `volt` state has settled. Manual upload flow leaves
    // overrides empty and reads the current state as before.
    // `forceDownload` (Download ZIP flow) opts back into the automatic
    // ZIP download at the end even though files came in via overrides.
    const _cams = overrides.camsFile !== undefined ? overrides.camsFile : cams;
    const _karvy = overrides.karvyFile !== undefined ? overrides.karvyFile : karvy;
    const _volt = overrides.voltFile !== undefined ? overrides.voltFile : volt;
    const auto = !!(overrides.camsFile || overrides.karvyFile || overrides.voltFile);
    const skipAutoDownload = auto && !overrides.forceDownload;
    // Track which card kicked this off so we can scope the progress banner.
    setFlowSource(auto ? "auto" : "manual");

    if (!_cams && !_karvy && !_volt) {
      toast.error("Please upload at least one file (CAMS, KARVY, or VOLT).");
      return;
    }
    const attached = [_cams && "CAMS", _karvy && "KARVY (MFSD205)", _volt && "VOLT"].filter(Boolean);
    // Auto-chained runs skip the interactive partial-source guard — the
    // caller has already committed to a single-source run.
    if (!auto && attached.length < 3) {
      const missing = ["CAMS", "KARVY (MFSD205)", "VOLT"].filter(
        (s) => !attached.some((a) => a.startsWith(s.split(" ")[0])),
      );
      const proceed = window.confirm(
        `You have attached only: ${attached.join(", ")}.\n\n` +
          `The following source(s) are NOT attached: ${missing.join(", ")}.\n\n` +
          `Processing will auto-start on the LAST chunk of the attached source(s), ` +
          `so you will NOT be able to add the missing files to this run.\n\n` +
          `Click OK to proceed with ${attached.join(" + ")} only.\n` +
          `Click Cancel to go back and attach the remaining files first.`,
      );
      if (!proceed) return;
    }
    const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    const jobId = newJobId();
    localStorage.setItem(LS_KEY, jobId);
    setProgressBySource({});
    setJobMessage("");
    if (!processSource) setProcessSource("manual");
    setStage("uploading");
    try {
      const expectedSources = [_cams && "cams", _karvy && "karvy", _volt && "volt"].filter(Boolean);
      if (_cams) await uploadFileChunked(jobId, "cams", _cams, authHeader, expectedSources, period);
      if (_karvy) await uploadFileChunked(jobId, "karvy", _karvy, authHeader, expectedSources, period);
      if (_volt) await uploadFileChunked(jobId, "volt", _volt, authHeader, expectedSources, period);

      // Backend auto-starts the worker on the last chunk; we just begin polling.
      setStage("processing");

      const status = await new Promise((resolve, reject) =>
        pollUntilDone(jobId, authHeader, resolve, reject)
      );

      if (skipAutoDownload) {
        // Auto-chained per-source flow: don't force-download the ZIP. The
        // completed run appears in Recent Runs where the user downloads it.
        toast.success("Step 2 completed — download from Recent Runs when ready.");
        try { await fetchRuns(); } catch (_) { /* ignore */ }
        setStage("idle");
        return;
      }

      await downloadResult(jobId, status, authHeader);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Unknown error";
      toast.error(`Processing failed: ${detail}`);
      // Keep LS_KEY so user can recover; they may want to re-poll on next visit
    } finally {
      setProgressBySource({});
      setJobMessage("");
      setProcessSource(null);
      if (stage !== "downloading") setStage("idle");
    }
  };

  // Auto-detected period will refresh on tab-switch (useEffect below); the
  // scanner cron itself runs every 30 min server-side, so we also refresh
  // tracker data at that cadence so the "next scan in ~N min" badge decays
  // naturally without needing manual clicks.
  useEffect(() => {
    if (uploadMode !== "auto") return;
    const iv = setInterval(() => { fetchExpectedReports(); }, 60000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadMode]);

  const busy = stage !== "idle";

  // Auto Process tab: fetch the most-recent raw-upload period on mount and
  // whenever the operator switches into the Auto tab so the commission
  // period reflects the data we're about to process.
  useEffect(() => {
    if (uploadMode === "auto") {
      fetchAutoPeriod();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadMode]);

  // Sync the manual month/year pickers to the auto-detected period so
  // downstream `period` derives correctly and Step-2 filenames match the
  // month of the actual data being processed.
  useEffect(() => {
    if (uploadMode !== "auto" || !autoPeriod?.period) return;
    const m = /^(\d{4})_(\d{2})$/.exec(autoPeriod.period);
    if (!m) return;
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    if (y && mo && (y !== periodYear || mo !== periodMonth)) {
      setPeriodYear(y);
      setPeriodMonth(mo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadMode, autoPeriod]);

  return (
    <div className="container mx-auto px-4 py-6" data-testid="commissions-page">
      <header className="mb-6 flex items-center gap-3">
        <Wallet className="h-7 w-7 text-amber-600" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Commission Management</h1>
          <p className="text-sm text-slate-500">
            Upload monthly CAMS, KARVY & VOLT files. Get one Excel per sub-broker, zipped together.
          </p>
          <p className="mt-1 text-xs text-amber-700" data-testid="commissions-protection-note">
            Each per-sub-broker Excel is password-protected with the sub-broker code (e.g.&nbsp;
            <code className="rounded bg-amber-50 px-1">1994101</code>). PAN is auto-injected on CAMS / KARVY / VOLT rows (VOLT uses name-similarity matching) and Total_ClientWise is grouped by PAN.
          </p>
        </div>
      </header>

      {/* Auto vs Manual as the top-level tabs. Step 1 (Expected Report /
          Gmail auto-fetch) lives inside Auto since only the auto pipeline
          depends on the raw dumps it produces. */}
      <Tabs value={uploadMode} onValueChange={setUploadMode} className="mb-4">
        <TabsList data-testid="upload-mode-tabs">
          <TabsTrigger value="auto" className="flex items-center gap-2" data-testid="upload-mode-auto">
            <RefreshCw className="w-4 h-4" />
            Auto Process
          </TabsTrigger>
          <TabsTrigger value="manual" className="flex items-center gap-2" data-testid="upload-mode-manual">
            <Upload className="w-4 h-4" />
            Manual Upload
          </TabsTrigger>
          <TabsTrigger value="tagging" className="flex items-center gap-2" data-testid="upload-mode-tagging">
            <FileText className="w-4 h-4" />
            Sub-Broker Tagging
          </TabsTrigger>
          <TabsTrigger value="exclusions" className="flex items-center gap-2" data-testid="upload-mode-exclusions">
            <X className="w-4 h-4" />
            Exclusions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="auto">
      <Card className="mb-6" data-testid="commissions-step1-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-lg">
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white">Step 1</Badge>
              <Mail className="h-5 w-5" />
              Expected Report — CAMS / KARVY auto-fetch from Gmail
            </div>
            {proxyStatus?.configured && (
              <div className="flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-normal"
                   title={`Exit IP: ${proxyStatus.exit_ip || '?'} — provider: ${proxyStatus.provider || 'IPRoyal'}`}
                   data-testid="cams-proxy-badge">
                <span className={`inline-block h-2 w-2 rounded-full ${proxyStatus.healthy ? "bg-emerald-500" : "bg-rose-500"}`}></span>
                <span className="text-emerald-700">
                  CAMS proxy · {proxyStatus.city || "IN"} · {proxyStatus.exit_ip || "?"}
                </span>
                {proxyStatus.renewal_date && (() => {
                  const days = Math.ceil((new Date(proxyStatus.renewal_date) - new Date()) / 86400000);
                  const cls = days <= 7 ? "text-rose-700" : days <= 21 ? "text-amber-700" : "text-slate-500";
                  return <span className={`ml-1 ${cls}`}>renews in {days}d</span>;
                })()}
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Add Expected Report form */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <div className="mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-700">Add Expected Report</span>
              <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">Watchlist</Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
              <div>
                <Label className="text-xs text-slate-500">Source</Label>
                <select
                  value={newReport.source}
                  onChange={(e) => {
                    const src = e.target.value;
                    setNewReport((p) => ({
                      ...p,
                      source: src,
                      add_to_db:
                        src === "CAMS" ? "WBR77_Raw" :
                        src === "VOLT" ? "VOLT_Raw" :
                                         "MFSD205_Raw",
                    }));
                  }}
                  disabled={adding}
                  data-testid="expected-source"
                  className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                >
                  <option value="CAMS">CAMS</option>
                  <option value="KARVY">KARVY</option>
                  <option value="VOLT">VOLT</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Add to DB</Label>
                <select
                  value={newReport.add_to_db}
                  onChange={(e) => setNewReport((p) => ({ ...p, add_to_db: e.target.value }))}
                  disabled={adding}
                  data-testid="expected-addtodb"
                  className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                >
                  {(newReport.source === "CAMS" ? ["WBR77_Raw"]
                    : newReport.source === "VOLT" ? ["VOLT_Raw"]
                    : ["MFSD205_Raw"]).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">
                  {newReport.source === "VOLT" ? "Google Sheet URL" : "Reference Number"}
                </Label>
                <Input
                  value={newReport.source === "VOLT" ? newReport.drive_url : newReport.reference_no}
                  onChange={(e) => setNewReport((p) => (
                    p.source === "VOLT"
                      ? { ...p, drive_url: e.target.value }
                      : { ...p, reference_no: e.target.value }
                  ))}
                  placeholder={newReport.source === "VOLT"
                    ? "https://docs.google.com/spreadsheets/d/…/edit?usp=sharing"
                    : "Request Id e.g. 224986392R77"}
                  disabled={adding}
                  data-testid="expected-refno"
                  className="mt-1 h-9"
                />
              </div>
              {newReport.source !== "VOLT" && (
                <div>
                  <Label className="text-xs text-slate-500">File Type</Label>
                  <select
                    value={newReport.file_type}
                    onChange={(e) => setNewReport((p) => ({ ...p, file_type: e.target.value }))}
                    disabled={adding}
                    data-testid="expected-filetype"
                    className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                  >
                    <option value="ZIP">ZIP (Password Protected)</option>
                    <option value="DBF">DBF</option>
                    <option value="CSV">CSV</option>
                    <option value="XLSX">XLSX</option>
                  </select>
                </div>
              )}
              {newReport.source !== "VOLT" && (
                <div>
                  <Label className="text-xs text-slate-500">Password</Label>
                  <Input
                    value={newReport.password}
                    onChange={(e) => setNewReport((p) => ({ ...p, password: e.target.value }))}
                    disabled={adding}
                    data-testid="expected-password"
                    className="mt-1 h-9"
                  />
                </div>
              )}
              <div>
                <Label className="text-xs text-slate-500">Frequency</Label>
                <div className="mt-1 flex items-center gap-2">
                  <div
                    className="h-9 flex-1 flex items-center rounded-md border border-slate-200 bg-slate-50 px-2 text-sm text-slate-700"
                    data-testid="expected-frequency"
                  >
                    One Time
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">
                <span className="font-semibold text-slate-700">How it works:</span> Paste the KARVY
                Ref.no. (from the subject line, e.g. <code className="rounded bg-slate-100 px-1">Brokerage Report for Ref.no. WBBRR2864584</code>). The system auto-scans Gmail every 30 min, downloads the ZIP via the Click Here link and writes every row into
                <code className="rounded bg-slate-100 px-1">{newReport.add_to_db}</code>. CAMS reports must be uploaded manually below (mailback links are India-only).
              </p>
              <Button
                onClick={addExpectedReport}
                disabled={adding || (newReport.source === "VOLT"
                  ? !newReport.drive_url?.trim()
                  : !newReport.reference_no?.trim())}
                data-testid="expected-add-btn"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {adding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                Add
              </Button>
            </div>
          </div>

          {/* Report Status Tracker (separate card below Watchlist) */}
          <div className="rounded-xl border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-100 p-3">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-700">Watchlist</span>
                <Badge
                  variant="outline"
                  className="bg-teal-50 text-teal-700 border-teal-200"
                  data-testid="commissions-autoscan-badge"
                  title={scannerLastRunAt
                    ? `Last auto-scan: ${new Date(scannerLastRunAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}`
                    : "Auto-scan runs every 30 min in the background"}
                >
                  Auto-scan · every 30 min{nextAutoScanMinutes != null ? ` · next in ~${nextAutoScanMinutes}m` : ""}
                </Badge>
                {(() => {
                  const currentCount = expectedReports.filter((r) => r.status === "pending").length;
                  const historyCount = expectedReports.filter((r) => r.status !== "pending" && r.status !== "cancelled").length;
                  return (
                    <div className="ml-2 flex items-center gap-1 rounded-full bg-slate-100 p-0.5">
                      <button
                        onClick={() => setWatchlistTab("current")}
                        data-testid="watchlist-tab-current"
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          watchlistTab === "current"
                            ? "bg-white text-slate-800 shadow-sm"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        Current <span className="ml-1 text-[10px] text-slate-400">{currentCount}</span>
                      </button>
                      <button
                        onClick={() => setWatchlistTab("historical")}
                        data-testid="watchlist-tab-historical"
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          watchlistTab === "historical"
                            ? "bg-white text-slate-800 shadow-sm"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        Historical <span className="ml-1 text-[10px] text-slate-400">{historyCount}</span>
                      </button>
                    </div>
                  );
                })()}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={scanExpectedReports}
                  disabled={scanning || expectedReports.length === 0}
                  data-testid="expected-scan-btn"
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {scanning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                  Scan
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={fetchExpectedReports}
                  disabled={loadingExpected}
                  data-testid="expected-refresh-btn"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${loadingExpected ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
              </div>
            </div>
            {(() => {
              const filtered = expectedReports.filter((r) =>
                watchlistTab === "current"
                  ? r.status === "pending"
                  : r.status !== "pending" && r.status !== "cancelled"
              );
              if (filtered.length === 0) {
                return (
                  <div className="p-6 text-center text-sm text-slate-400">
                    <FileText className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                    {watchlistTab === "current"
                      ? "No pending references. Add one above to begin."
                      : "No received/read references yet. Once Scan matches an email, its watchlist row moves here."}
                  </div>
                );
              }
              return (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Reference</th>
                        <th className="px-3 py-2 text-left">Source</th>
                        <th className="px-3 py-2 text-left">Target DB</th>
                        <th className="px-3 py-2 text-left">File Type</th>
                        <th className="px-3 py-2 text-left">Frequency</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">
                          {watchlistTab === "current" ? "Added" : "Received"}
                        </th>
                        <th className="px-3 py-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((r) => (
                        <tr key={r.id} className="border-t border-slate-100" data-testid={`watchlist-row-${r.reference_no}`}>
                          <td className="px-3 py-2 font-mono font-semibold text-slate-700">{r.reference_no}</td>
                          <td className="px-3 py-2"><Badge variant="outline">{r.source}</Badge></td>
                          <td className="px-3 py-2"><Badge variant="secondary">{r.add_to_db}</Badge></td>
                          <td className="px-3 py-2 text-slate-600">{r.file_type}</td>
                          <td className="px-3 py-2 text-slate-600">{r.frequency === "one_time" ? "One Time" : "Daily"}</td>
                          <td className="px-3 py-2">
                            <Badge className={
                              r.status === "read"     ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                              r.status === "received" ? "bg-amber-100 text-amber-700 border-amber-200"       :
                                                         "bg-slate-100 text-slate-600 border-slate-200"
                            }>
                              {r.status}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-xs text-slate-500">
                            {(watchlistTab === "historical" ? r.received_at : r.created_at)
                              ? new Date(watchlistTab === "historical" ? r.received_at : r.created_at).toLocaleString("en-IN", {
                                  timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                                })
                              : "—"}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              onClick={() => deleteExpectedReport(r.id)}
                              className="text-rose-500 hover:text-rose-700"
                              title="Remove"
                              data-testid={`expected-del-${r.reference_no}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>

          {/* Report Status Tracker — populated after Scan matches */}
          <div className="rounded-xl border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-100 p-3">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-700">Report Status Tracker</span>
                {tracker.length > 0 && (
                  <Badge variant="outline">{tracker.length} email{tracker.length === 1 ? "" : "s"}</Badge>
                )}
              </div>
            </div>

            {tracker.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">
                <FileText className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                No emails matched yet. Auto-scan runs every 30 minutes in the background — new emails appear here automatically. Click <b>Scan</b> above only if you need to trigger an immediate scan.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Reference</th>
                      <th className="px-3 py-2 text-left">DB</th>
                      <th className="px-3 py-2 text-left">Email Date</th>
                      <th className="px-3 py-2 text-center">Received</th>
                      <th className="px-3 py-2 text-center">Read</th>
                      <th className="px-3 py-2 text-center">Records</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tracker.map((e, idx) => (
                      <tr key={e.email_uid || idx} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-mono font-semibold text-slate-700">{e.reference_no}</td>
                        <td className="px-3 py-2"><Badge variant="secondary">{e.add_to_db}</Badge></td>
                        <td className="px-3 py-2 text-xs text-slate-600">
                          {e.email_date
                            ? new Date(e.email_date).toLocaleString("en-IN", {
                                timeZone: "Asia/Kolkata",
                                day: "2-digit", month: "short", year: "numeric",
                                hour: "2-digit", minute: "2-digit",
                              })
                            : "—"}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {e.received_at ? (
                            <CheckCircle2 className="mx-auto h-5 w-5 text-emerald-500" />
                          ) : (
                            <Clock className="mx-auto h-5 w-5 text-slate-300" />
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {e.status === "read" ? (
                            <CheckCircle2 className="mx-auto h-5 w-5 text-emerald-500" />
                          ) : e.scan_error ? (
                            <span className="cursor-help" title={e.scan_error} data-testid={`tracker-err-${idx}`}>
                              <AlertTriangle className="mx-auto h-5 w-5 text-rose-500" />
                            </span>
                          ) : (
                            <Loader2 className="mx-auto h-5 w-5 animate-spin text-amber-500" />
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {e.status === "read" && e.records_count > 0 ? (
                            <span className="font-semibold text-slate-800">
                              {e.records_count.toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Auto Process card — pulls raw rows fetched by the Expected Report
          watchlist above and auto-runs Step 2 for KARVY / VOLT. */}
          <Card className="mb-6 border-emerald-200" data-testid="commissions-auto-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-lg">
                <span className="flex items-center gap-2">
                  <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white">Step 2</Badge>
                  <RefreshCw className="h-5 w-5" />
                  Prepare Commission Files
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Auto-detected period</span>
                  <Badge
                    variant="outline"
                    className="bg-emerald-50 text-emerald-800 border-emerald-200"
                    data-testid="commissions-auto-period-badge"
                  >
                    {autoPeriodLoading
                      ? "Detecting…"
                      : autoPeriod?.period
                        ? `${periodLabel(autoPeriod.period)} · ${autoPeriod.period}`
                        : `${periodLabel(period)} · ${period}`}
                  </Badge>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={fetchAutoPeriod}
                    disabled={busy || autoPeriodLoading}
                    data-testid="commissions-auto-period-refresh"
                    className="h-7"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${autoPeriodLoading ? "animate-spin" : ""}`} />
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <AutopilotStep2Panel ap={autopilot} onTagKarvy={openKarvyGapModal} onTagVolt={openVoltGapModal} />
              {step2Hidden && (
                <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800" data-testid="step2-waiting-new-month">
                  New watchlist added — last month's files are hidden. Waiting for this month's files; the Auto-Process Pipeline above will build them automatically.
                </div>
              )}
              <p className="text-sm text-slate-600">
                Transforms the raw dumps in <code className="rounded bg-slate-100 px-1">MFSD205_Raw</code> /
                <code className="mx-1 rounded bg-slate-100 px-1">VOLTMONEY_Raw</code> /
                <code className="rounded bg-slate-100 px-1">WBR77_Raw</code> into the DB-formatted collections
                (MFSD205, VOLTMONEY, WBR77) and refreshes the download caches. Click <b>Prepare Commission Files</b>{" "}
                after every fresh Step 1 email scan, then use the tiles below to grab the prescribed KARVY / VOLT workbooks.
              </p>
              <div className="flex items-end justify-end gap-2">
                <Button
                  onClick={handlePrepareCommissionFiles}
                  disabled={busy || prepareBusy}
                  size="lg"
                  data-testid="commissions-prepare-btn"
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {prepareBusy ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{prepareMsg || "Preparing…"}</>
                  ) : (
                    <><RefreshCw className="mr-2 h-4 w-4" /> Prepare Commission Files</>
                  )}
                </Button>
                {prepareBusy && (
                  <Button
                    onClick={handleForceStopPrepare}
                    size="lg"
                    variant="outline"
                    data-testid="commissions-prepare-force-stop-btn"
                    className="border-red-300 bg-white text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Force stop
                  </Button>
                )}
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">CAMS</span>
                    <Badge variant="outline" className={`text-xs ${
                      prepareStatus.cams === "ready" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                        : prepareStatus.cams === "running" ? "border-amber-300 bg-amber-50 text-amber-700"
                        : prepareStatus.cams === "error" ? "border-red-300 bg-red-50 text-red-700"
                        : prepareStatus.cams === "skipped" ? "border-slate-300 bg-slate-100 text-slate-500"
                        : "border-slate-300 bg-slate-100 text-slate-500"}`}>
                      {prepareStatus.cams === "ready" ? "Ready"
                        : prepareStatus.cams === "running" ? "Building…"
                        : prepareStatus.cams === "error" ? "Failed"
                        : prepareStatus.cams === "skipped" ? "No raw data"
                        : "Not prepared"}
                    </Badge>
                  </div>
                  <p className="mb-3 text-xs text-slate-500">
                    {prepareStatus.cams === "running"
                      ? (prepareMsg?.startsWith("CAMS") ? prepareMsg : "Building CAMS workbook…")
                      : prepareStatus.cams === "ready"
                        ? "Cached CAMS workbook — click to download."
                        : "Click Prepare Commission Files above to build the cache."}
                  </p>
                  {prepareStatus.cams === "running" && (
                    <div className="mb-3" data-testid="commissions-cams-progress">
                      <div className="mb-1 flex items-center justify-between text-[11px] font-medium text-amber-800">
                        <span>{prepareProgress.cams.percent || 0}% · {prepareProgress.cams.message || "Building…"}</span>
                        <span>{prepareProgress.cams.elapsed}s</span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded bg-amber-100">
                        <div
                          className="h-full bg-amber-500 transition-all"
                          style={{ width: `${Math.max(2, prepareProgress.cams.percent || 0)}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {prepareStatus.cams === "ready" && prepareBuiltAt.cams && (
                    <p className="mb-2 text-[11px] text-slate-400" data-testid="commissions-cams-built-at">
                      Last rebuilt: {formatBuiltAt(prepareBuiltAt.cams)} IST
                    </p>
                  )}
                  <Button
                    type="button" size="sm"
                    onClick={handleDownloadCams}
                    disabled={busy || camsDownloading || prepareStatus.cams !== "ready"}
                    data-testid="commissions-cams-download-btn"
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    {prepareStatus.cams === "running" ? (
                      <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Building…</>
                    ) : camsDownloading ? (
                      <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> {camsDownloadMsg || "Preparing…"}</>
                    ) : (
                      <><Download className="mr-1.5 h-3.5 w-3.5" /> Download CAMS</>
                    )}
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => handlePrepareCommissionFiles({ only: "cams", skipConfirm: true })}
                    disabled={prepareBusy || prepareStatus.cams === "skipped"}
                    data-testid="commissions-cams-rebuild-btn"
                    className="mt-2 w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  >
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                    Rebuild CAMS only
                  </Button>
                  {unresolvedCounts.cams > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={openCamsGapModal}
                      data-testid="commissions-cams-gap-btn"
                      className="mt-2 w-full border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                    >
                      <AlertTriangle className="mr-1.5 h-3.5 w-3.5" /> Map unresolved folios ({unresolvedCounts.cams})
                    </Button>
                  )}
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">KARVY / KFintech</span>
                    <Badge variant="outline" className={`text-xs ${
                      prepareStatus.karvy === "ready" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                        : prepareStatus.karvy === "running" ? "border-amber-300 bg-amber-50 text-amber-700"
                        : prepareStatus.karvy === "error" ? "border-red-300 bg-red-50 text-red-700"
                        : prepareStatus.karvy === "skipped" ? "border-slate-300 bg-slate-100 text-slate-500"
                        : "border-slate-300 bg-slate-100 text-slate-500"}`}>
                      {prepareStatus.karvy === "ready" ? "Ready"
                        : prepareStatus.karvy === "running" ? "Building…"
                        : prepareStatus.karvy === "error" ? "Failed"
                        : prepareStatus.karvy === "skipped" ? "No raw data"
                        : "Not prepared"}
                    </Badge>
                  </div>
                  <p className="mb-3 text-xs text-slate-500">
                    {prepareStatus.karvy === "running"
                      ? (prepareMsg?.startsWith("KARVY") ? prepareMsg : "Building KARVY workbook…")
                      : prepareStatus.karvy === "ready"
                        ? "Cached KARVY workbook — click to download."
                        : "Click Prepare Commission Files above to build the cache."}
                  </p>
                  {prepareStatus.karvy === "running" && (
                    <div className="mb-3" data-testid="commissions-karvy-progress">
                      <div className="mb-1 flex items-center justify-between text-[11px] font-medium text-amber-800">
                        <span>{prepareProgress.karvy.percent || 0}% · {prepareProgress.karvy.message || "Building…"}</span>
                        <span>{prepareProgress.karvy.elapsed}s</span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded bg-amber-100">
                        <div
                          className="h-full bg-amber-500 transition-all"
                          style={{ width: `${Math.max(2, prepareProgress.karvy.percent || 0)}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {prepareStatus.karvy === "ready" && prepareBuiltAt.karvy && (
                    <p className="mb-2 text-[11px] text-slate-400" data-testid="commissions-karvy-built-at">
                      Last rebuilt: {formatBuiltAt(prepareBuiltAt.karvy)} IST
                    </p>
                  )}
                  <Button
                    type="button" size="sm"
                    onClick={handleDownloadKarvy}
                    disabled={busy || karvyDownloading || prepareStatus.karvy !== "ready"}
                    data-testid="commissions-karvy-download-btn"
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    {prepareStatus.karvy === "running" ? (
                      <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Building…</>
                    ) : karvyDownloading ? (
                      <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> {karvyDownloadMsg || "Preparing…"}</>
                    ) : (
                      <><Download className="mr-1.5 h-3.5 w-3.5" /> Download KARVY</>
                    )}
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => handlePrepareCommissionFiles({ only: "karvy", skipConfirm: true })}
                    disabled={prepareBusy || prepareStatus.karvy === "skipped"}
                    data-testid="commissions-karvy-rebuild-btn"
                    className="mt-2 w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  >
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                    Rebuild KARVY only
                  </Button>
                  {unresolvedCounts.karvy > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={openKarvyUnresolvedOnDemand}
                      data-testid="commissions-karvy-gap-btn"
                      className="mt-2 w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                    >
                      <AlertTriangle className="mr-1.5 h-3.5 w-3.5" /> Map unresolved folios ({unresolvedCounts.karvy})
                    </Button>
                  )}
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">VOLT</span>
                    <Badge variant="outline" className={`text-xs ${
                      prepareStatus.volt === "ready" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                        : prepareStatus.volt === "running" ? "border-amber-300 bg-amber-50 text-amber-700"
                        : prepareStatus.volt === "error" ? "border-red-300 bg-red-50 text-red-700"
                        : prepareStatus.volt === "skipped" ? "border-slate-300 bg-slate-100 text-slate-500"
                        : "border-slate-300 bg-slate-100 text-slate-500"}`}>
                      {prepareStatus.volt === "ready" ? "Ready"
                        : prepareStatus.volt === "running" ? "Building…"
                        : prepareStatus.volt === "error" ? "Failed"
                        : prepareStatus.volt === "skipped" ? "No raw data"
                        : "Not prepared"}
                    </Badge>
                  </div>
                  <p className="mb-3 text-xs text-slate-500">
                    {prepareStatus.volt === "running"
                      ? (prepareMsg?.startsWith("VOLT") ? prepareMsg : "Building VOLT workbook…")
                      : prepareStatus.volt === "ready"
                        ? "Cached VOLT workbook — click to download."
                        : "Click Prepare Commission Files above to build the cache."}
                  </p>
                  {prepareStatus.volt === "running" && (
                    <div className="mb-3" data-testid="commissions-volt-progress">
                      <div className="mb-1 flex items-center justify-between text-[11px] font-medium text-amber-800">
                        <span>{prepareProgress.volt.percent || 0}% · {prepareProgress.volt.message || "Building…"}</span>
                        <span>{prepareProgress.volt.elapsed}s</span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded bg-amber-100">
                        <div
                          className="h-full bg-amber-500 transition-all"
                          style={{ width: `${Math.max(2, prepareProgress.volt.percent || 0)}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {prepareStatus.volt === "ready" && prepareBuiltAt.volt && (
                    <p className="mb-2 text-[11px] text-slate-400" data-testid="commissions-volt-built-at">
                      Last rebuilt: {formatBuiltAt(prepareBuiltAt.volt)} IST
                    </p>
                  )}
                  <Button
                    type="button" size="sm"
                    onClick={handleDownloadVolt}
                    disabled={busy || voltDownloading || prepareStatus.volt !== "ready"}
                    data-testid="commissions-volt-download-btn"
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    {prepareStatus.volt === "running" ? (
                      <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Building…</>
                    ) : voltDownloading ? (
                      <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> {voltDownloadMsg || "Preparing…"}</>
                    ) : (
                      <><Download className="mr-1.5 h-3.5 w-3.5" /> Download VOLT</>
                    )}
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => handlePrepareCommissionFiles({ only: "volt", skipConfirm: true })}
                    disabled={prepareBusy || prepareStatus.volt === "skipped"}
                    data-testid="commissions-volt-rebuild-btn"
                    className="mt-2 w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  >
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                    Rebuild VOLT only
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ─── Step 3 — Generate per-Sub-Broker ZIP ───────────────────
              Lifted out of the Step 2 card into its own sibling card so
              the two phases (prepare cache vs. compile ZIP) are visually
              and logically separate. Uses the same handleAutoProcessZip
              flow that pulls the KARVY / VOLT cached workbooks. */}
          <Card className="mb-6 border-emerald-200" data-testid="commissions-auto-zip-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-lg">
                <span className="flex items-center gap-2">
                  <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white">Step 3</Badge>
                  <Download className="h-5 w-5" />
                  Generate per-Sub-Broker ZIP
                </span>
                <Button
                  onClick={handleAutoProcessZip}
                  disabled={busy || autoPeriod?.source === "fallback" || (() => {
                    // Only enable once every non-skipped tile from Step 2 is
                    // "ready" — otherwise Step 3 would try to zip incomplete
                    // caches. `skipped` means the raw source is absent, so
                    // it's not blocking. Any `idle`/`running`/`error` state
                    // means Step 2 hasn't finished yet.
                    const s = prepareStatus;
                    const gate = (v) => v === "ready" || v === "skipped";
                    return !(gate(s.karvy) && gate(s.volt) && gate(s.cams));
                  })()}
                  size="lg"
                  data-testid="commissions-auto-process-zip-btn"
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {busy ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {stage === "uploading" && "Uploading…"}
                      {stage === "processing" && "Processing…"}
                      {stage === "downloading" && "Downloading…"}
                      {stage === "idle" && "Preparing…"}
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" />
                      Download ZIP
                    </>
                  )}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <AutopilotStep3Panel ap={autopilot}
                onDownloadZip={(jid) => downloadResult(jid, null, { Authorization: `Bearer ${localStorage.getItem("token")}` })} />
              <p className="text-xs text-slate-500">
                Same Step-2 pipeline as the Manual tab — the only difference is the KARVY / VOLT
                workbooks come from the cached files prepared in Step 2 above, so no re-upload is needed.
              </p>

              {busy && flowSource === "auto" && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="commissions-auto-progress-banner">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 font-semibold">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {stage === "idle" && "Preparing sub-broker wise files…"}
                      {stage === "uploading" && "Handing off Step 2 files (no re-upload)…"}
                      {stage === "processing" && "Server is splitting rows per sub-broker & zipping…"}
                      {stage === "downloading" && "Streaming ZIP to your browser"}
                    </div>
                    {(stage === "processing" || stage === "uploading") && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleForceKill}
                        data-testid="commissions-auto-force-kill-btn"
                        className="h-7 border-red-300 bg-white text-xs text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="mr-1 h-3.5 w-3.5" />
                        Force kill
                      </Button>
                    )}
                  </div>
                  {jobMessage && (
                    <p className="ml-6 mt-1 text-xs text-amber-800">{jobMessage}</p>
                  )}
                  <p className="ml-6 mt-1 text-xs text-amber-800">
                    Files are pre-cached — this typically completes in ~1–2 min. Don&apos;t close this tab. If progress stalls for {">"}5 min, use Force kill.
                  </p>
                </div>
              )}

              {!busy && (
                <div className={`flex items-start gap-2 rounded-lg p-3 text-xs ${
                  autoPeriod?.source === "fallback"
                    ? "bg-amber-50 text-amber-800"
                    : ((prepareStatus.karvy === "ready" || prepareStatus.karvy === "skipped") &&
                       (prepareStatus.volt === "ready"  || prepareStatus.volt === "skipped") &&
                       (prepareStatus.cams === "ready"  || prepareStatus.cams === "skipped"))
                      ? "bg-emerald-50 text-emerald-800"
                      : "bg-slate-50 text-slate-700"
                }`}>
                  <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                    autoPeriod?.source === "fallback" ? "text-amber-500" : "text-emerald-500"
                  }`} />
                  <span>
                    {autoPeriod?.source === "fallback" ? (
                      <>Waiting for Step 1 to complete. Once the auto-scan (every 30 min) fetches emails into the raw collections, <b>Download ZIP</b> will auto-process KARVY + VOLT and hand you the per-sub-broker ZIP.</>
                    ) : ((prepareStatus.karvy === "ready" || prepareStatus.karvy === "skipped") &&
                         (prepareStatus.volt === "ready"  || prepareStatus.volt === "skipped") &&
                         (prepareStatus.cams === "ready"  || prepareStatus.cams === "skipped")) ? (
                      <>Step 2 caches are ready. Click <b>Download ZIP</b> to compile the per-sub-broker password-protected ZIP from the cached workbooks.</>
                    ) : (
                      <>Run <b>Step 2 · Prepare Commission Files</b> first. This button unlocks once every available RTA tile shows a <b>Ready</b> badge.</>
                    )}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="manual">
      <Card className="mb-6" data-testid="commissions-manual-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white">Step 1 · Manual</Badge>
            <Upload className="h-5 w-5" />
            Upload monthly commission files
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 md:grid-cols-3">
            <FileSlot
              label="CAMS"
              hint="Monthly CAMS commission file (.xlsx). Large files (150 MB+) are uploaded in chunks."
              file={cams}
              onChange={setCams}
              disabled={busy}
              testId="commissions-cams-input"
              uploadedPct={progressBySource.cams}
            />
            <FileSlot
              label="KARVY / KFintech"
              hint="Monthly KARVY commission file (.xlsx)."
              file={karvy}
              onChange={setKarvy}
              disabled={busy}
              testId="commissions-karvy-input"
              uploadedPct={progressBySource.karvy}
            />
            <FileSlot
              label="VOLT"
              hint="Monthly VOLT export (.xlsx)."
              file={volt}
              onChange={setVolt}
              disabled={busy}
              testId="commissions-volt-input"
              uploadedPct={progressBySource.volt}
            />
          </div>

          <div className="grid items-end gap-3 md:grid-cols-[1fr_auto]">
            <div>
              <Label className="text-sm font-semibold text-slate-700">
                Commission period
              </Label>
              <p className="mt-1 text-xs text-slate-500">
                Pick the month + year these files belong to. Used in filenames like{" "}
                <code>{period ? `${period}_<sub-broker>.xlsx` : "YYYY_MM_<sub-broker>.xlsx"}</code>.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <select
                  value={periodMonth}
                  onChange={(e) => setPeriodMonth(parseInt(e.target.value, 10))}
                  disabled={busy}
                  data-testid="commissions-period-month"
                  className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  {MONTH_LABELS.map((mn, i) => (
                    <option key={mn} value={i + 1}>
                      {mn}
                    </option>
                  ))}
                </select>
                <select
                  value={periodYear}
                  onChange={(e) => setPeriodYear(parseInt(e.target.value, 10))}
                  disabled={busy}
                  data-testid="commissions-period-year"
                  className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  {Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 3 + i).map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                <Badge
                  variant="outline"
                  className="bg-amber-50 text-amber-800 border-amber-200"
                  data-testid="commissions-period-preview"
                >
                  {periodLabel(period)} · {period}
                </Badge>
              </div>
            </div>
            <Button
              onClick={handleProcess}
              disabled={busy || (!cams && !karvy && !volt)}
              size="lg"
              data-testid="commissions-process-btn"
              className="bg-amber-600 hover:bg-amber-700"
            >
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {stage === "uploading" && "Uploading…"}
                  {stage === "processing" && "Processing…"}
                  {stage === "downloading" && "Downloading…"}
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Process & Download ZIP
                </>
              )}
            </Button>
          </div>

          {busy && processSource === "manual" && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="commissions-progress-banner">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 font-semibold">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {stage === "uploading" && "Uploading files in 4 MB chunks"}
                  {stage === "processing" && "Server is parsing your files and generating the ZIP"}
                  {stage === "downloading" && "Streaming ZIP to your browser"}
                </div>
                {(stage === "processing" || stage === "uploading") && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleForceKill}
                    data-testid="commissions-force-kill-btn"
                    className="h-7 border-red-300 bg-white text-xs text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="mr-1 h-3.5 w-3.5" />
                    Force kill
                  </Button>
                )}
              </div>
              {jobMessage && (
                <p className="ml-6 mt-1 text-xs text-amber-800">{jobMessage}</p>
              )}
              <p className="ml-6 mt-1 text-xs text-amber-800">
                Don&apos;t close this tab. CAMS+KARVY can take 5–8 minutes end-to-end. If progress stalls for {">"}5 min, use Force kill to reset.
              </p>
            </div>
          )}

          {!busy && (
            <div className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-slate-400" />
              <span>
                Files are uploaded in 4 MB chunks to avoid ingress timeouts. Processing
                happens server-side asynchronously; the page polls until your ZIP is ready.
              </span>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="tagging">
          <SubBrokerTaggingTab />
        </TabsContent>
        <TabsContent value="exclusions">
          <ExclusionsTab />
        </TabsContent>
      </Tabs>

      {/* Step 2-5 pipeline + Recent runs only apply to the Auto/Manual
          upload flows — the Sub-Broker Tagging tab is self-contained
          and shouldn't show commission processing steps below it. */}
      {uploadMode !== "tagging" && uploadMode !== "exclusions" && (
        <>
          <Step2InvoiceAndEmail runs={runs} busy={busy} onChanged={fetchRuns} uploadMode={uploadMode} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2 text-lg">
            <span className="flex items-center gap-2">
              <History className="h-5 w-5 text-slate-600" />
              Recent runs
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
                try {
                  const r = await axios.post(`${API}/commissions/recover-orphans`, null, { headers: authHeader });
                  const n = (r.data?.recovered || []).length;
                  if (n > 0) toast.success(`Recovered ${n} stuck job(s) — they'll appear here when done.`);
                  else toast.info("No stuck jobs to recover.");
                  fetchRuns();
                } catch (e) {
                  toast.error(`Recovery failed: ${e.response?.data?.detail || e.message}`);
                }
              }}
              data-testid="commissions-recover-orphans-btn"
              disabled={busy}
            >
              Recover stuck jobs
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="commissions-no-runs">
              No commission runs yet. Upload files above to generate your first report.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-slate-500">
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 pr-3">User</th>
                    <th className="py-2 pr-3">Period</th>
                    <th className="py-2 pr-3">CAMS rows</th>
                    <th className="py-2 pr-3">KARVY rows</th>
                    <th className="py-2 pr-3">VOLT rows</th>
                    <th className="py-2 pr-3">Sub-brokers</th>
                    <th className="py-2 pr-3">ZIP size</th>
                    <th className="py-2 pr-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r, i) => {
                    const s = r.summary || {};
                    const subBrokers = Math.max(
                      s.cams_sub_brokers || 0,
                      s.karvy_sub_brokers || 0,
                      s.volt_sub_brokers || 0
                    );
                    return (
                      <tr key={`row-${i}`} className="border-b" data-testid={`commissions-run-row-${i}`}>
                        <td className="py-2 pr-3">{formatDateTime(r.run_at)}</td>
                        <td className="py-2 pr-3">{r.user_name || "—"}</td>
                        <td className="py-2 pr-3">
                          <Badge variant="outline">{r.period || "—"}</Badge>
                        </td>
                        <td className="py-2 pr-3">{(s.cams_rows || 0).toLocaleString()}</td>
                        <td className="py-2 pr-3">{(s.karvy_rows || 0).toLocaleString()}</td>
                        <td className="py-2 pr-3">{(s.volt_rows || 0).toLocaleString()}</td>
                        <td className="py-2 pr-3">{subBrokers}</td>
                        <td className="py-2 pr-3">{formatBytes(r.zip_size_bytes)}</td>
                        <td className="py-2 pr-3 text-right">
                          {r.job_id && r.available ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadExisting(r.job_id)}
                              disabled={busy}
                              data-testid={`commissions-run-download-${i}`}
                            >
                              <Download className="mr-1 h-3.5 w-3.5" />
                              Download
                            </Button>
                          ) : (
                            <span className="text-xs text-slate-400">expired</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AdminCleanupCard runs={runs} busy={busy} onCleaned={fetchRuns} />

      {/* KARVY sub-broker tagging modal — opened by handlePrepareCommissionFiles
          when the backend returns 409 unmapped_subbroker. Reuses the /save-gaps
          endpoint and auto-retries the full Prepare flow on save. Must live
          inside the Commissions component so its state is in scope. */}
      <Dialog
        open={karvyGapModal.open}
        onOpenChange={(o) => { if (!o) setKarvyGapModal({ open: false, gaps: [] }); }}
      >
        <DialogContent className="max-w-4xl" data-testid="commissions-prepare-karvy-gaps-modal">
          <DialogHeader>
            <DialogTitle>Map Sub-Broker ({karvyGapModal.gaps.length} accounts)</DialogTitle>
            <DialogDescription>
              These MFSD205_Raw accounts either have no Sub-Broker or carry a code that
              doesn&apos;t match any associate in the <code>subbroker</code> collection,
              and the <code>subbroker_mapping</code> PAN chain couldn&apos;t resolve them
              either. Tag as many as you can and click <b>Save</b> — remaining rows will
              stay open so you can continue later.
            </DialogDescription>
          </DialogHeader>
          {/* Bulk-apply picker: one-click set-all-blank-rows to a single associate. */}
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs">
            <span className="font-semibold text-slate-700">Apply to all blank rows:</span>
            <select
              onChange={(e) => { applyToAllKarvyGaps(e.target.value); e.target.value = ""; }}
              data-testid="prepare-karvy-apply-all"
              className="h-7 flex-1 min-w-[220px] rounded-md border border-slate-200 bg-white px-2 text-xs"
              defaultValue=""
            >
              <option value="">— pick associate to bulk-fill —</option>
              {karvyAssociates.map((a) => (
                <option key={a.code} value={a.code}>{a.code} — {a.name || a.email || "(unnamed)"}</option>
              ))}
            </select>
            <span className="ml-auto text-slate-600" data-testid="prepare-karvy-progress">
              {karvyTaggedCount} / {karvyGapModal.gaps.length} tagged
            </span>
          </div>
          <div className="max-h-[420px] overflow-auto rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-xs font-semibold text-slate-600 z-10">
                <tr>
                  <th className="px-3 py-2 text-left">Investor</th>
                  <th className="px-3 py-2 text-left">Account #</th>
                  <th className="px-3 py-2 text-left">Raw code (RTA)</th>
                  <th className="px-3 py-2 text-left w-64">Associate (Sub-Broker)</th>
                </tr>
              </thead>
              <tbody>
                {karvyGapModal.gaps.map((g, i) => (
                  <tr key={g.account_number} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-800">{g.investor_name || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{g.account_number}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {g.raw_sub_broker
                        ? <span className="rounded bg-rose-50 px-1.5 py-0.5 text-rose-700"
                                title={(g.raw_sub_broker_examples || []).join(", ")}>
                            {g.raw_sub_broker}
                            {g.raw_sub_broker_examples && g.raw_sub_broker_examples.length > 1
                              ? ` +${g.raw_sub_broker_examples.length - 1}` : ""}
                          </span>
                        : <span className="text-slate-400">(empty)</span>}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={g.sub_broker}
                        onChange={(e) => updateKarvyGap(i, e.target.value)}
                        data-testid={`prepare-karvy-gap-select-${i}`}
                        className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="">— select associate —</option>
                        {karvyAssociates.map((a) => (
                          <option key={a.code} value={a.code}>
                            {a.code} — {a.name || a.email || "(unnamed)"}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setKarvyGapModal({ open: false, gaps: [] })}
              disabled={karvySavingGaps}
              data-testid="prepare-karvy-gaps-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={saveKarvyGapsAndRetry}
              disabled={karvySavingGaps || karvyTaggedCount === 0}
              data-testid="prepare-karvy-gaps-save"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {karvySavingGaps ? (
                <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Saving…</>
              ) : karvyTaggedCount === 0 ? "Tag at least one row"
                : allKarvyGapsMapped ? `Save all ${karvyTaggedCount} & Retry Build`
                : `Save ${karvyTaggedCount} tagged (keep ${karvyGapModal.gaps.length - karvyTaggedCount} for later)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VOLT IFA tagging modal — opened by handlePrepareCommissionFiles
          when /prepare/volt returns 409 unmapped_ifa. Maps VOLT source_id
          (per-customer) to an associate code, then retries the full flow. */}
      <Dialog
        open={voltGapModal.open}
        onOpenChange={(o) => { if (!o) setVoltGapModal({ open: false, gaps: [] }); }}
      >
        <DialogContent className="max-w-4xl" data-testid="commissions-prepare-volt-gaps-modal">
          <DialogHeader>
            <DialogTitle>Map IFA ({voltGapModal.gaps.length} customers)</DialogTitle>
            <DialogDescription>
              These VOLT customers have no IFA (associate) mapping in either
              the historical <code>VOLTMONEY</code> collection or the
              <code>volt_ifa_overrides</code> table. Tag as many as you can and
              click <b>Save</b> — remaining rows will stay open so you can
              continue later.
            </DialogDescription>
          </DialogHeader>
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs">
            <span className="font-semibold text-slate-700">Apply to all blank rows:</span>
            <select
              onChange={(e) => { applyToAllVoltGaps(e.target.value); e.target.value = ""; }}
              data-testid="prepare-volt-apply-all"
              className="h-7 flex-1 min-w-[220px] rounded-md border border-slate-200 bg-white px-2 text-xs"
              defaultValue=""
            >
              <option value="">— pick associate to bulk-fill —</option>
              {karvyAssociates.map((a) => (
                <option key={a.code} value={a.code}>{a.code} — {a.name || a.email || "(unnamed)"}</option>
              ))}
            </select>
            <span className="ml-auto text-slate-600" data-testid="prepare-volt-progress">
              {voltTaggedCount} / {voltGapModal.gaps.length} tagged
            </span>
          </div>
          <div className="max-h-[420px] overflow-auto rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-xs font-semibold text-slate-600 z-10">
                <tr>
                  <th className="px-3 py-2 text-left">Customer</th>
                  <th className="px-3 py-2 text-left">Source ID</th>
                  <th className="px-3 py-2 text-left w-64">Associate (IFA)</th>
                </tr>
              </thead>
              <tbody>
                {voltGapModal.gaps.map((g, i) => (
                  <tr key={g.source_id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-800">{g.customer_name || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{g.source_id}</td>
                    <td className="px-3 py-2">
                      <select
                        value={g.ifa_code}
                        onChange={(e) => updateVoltGap(i, e.target.value)}
                        data-testid={`prepare-volt-gap-select-${i}`}
                        className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="">— select associate —</option>
                        {karvyAssociates.map((a) => (
                          <option key={a.code} value={a.code}>
                            {a.code} — {a.name || a.email || "(unnamed)"}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setVoltGapModal({ open: false, gaps: [] })}
              disabled={voltSavingGaps}
              data-testid="prepare-volt-gaps-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={saveVoltGapsAndRetry}
              disabled={voltSavingGaps || voltTaggedCount === 0}
              data-testid="prepare-volt-gaps-save"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {voltSavingGaps ? (
                <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Saving…</>
              ) : voltTaggedCount === 0 ? "Tag at least one row"
                : allVoltGapsMapped ? `Save all ${voltTaggedCount} & Retry Build`
                : `Save ${voltTaggedCount} tagged (keep ${voltGapModal.gaps.length - voltTaggedCount} for later)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CAMS unresolved-folio tagging modal — mirrors the VOLT flow.
          Opened by openCamsGapModal, lists every WBR77 folio whose PAN/name
          chain failed. Operator picks a SubBroker per folio (or bulk-apply)
          and Save persists to `cams_folio_subbroker_overrides`, which then
          takes precedence in the next preview/ZIP build. */}
      <Dialog
        open={camsGapModal.open}
        onOpenChange={(o) => { if (!o) setCamsGapModal({ open: false, rows: [], options: [], loading: false, bulk: "" }); }}
      >
        <DialogContent className="max-w-5xl" data-testid="commissions-cams-gaps-modal">
          <DialogHeader>
            <DialogTitle>
              Map unresolved CAMS folios ({camsGapModal.rows.length})
            </DialogTitle>
            <DialogDescription>
              These WBR77 rows could not be resolved via the PAN chain
              (folio → investor_master → PAN → subbroker_mapping) or the
              name-fallback chain. Tag each folio to a SubBroker — saved
              mappings take precedence over the PAN chain on the next
              CAMS preview / ZIP build. You can tag any subset and come
              back for the rest later.
            </DialogDescription>
          </DialogHeader>

          {camsGapModal.loading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading unresolved folios…
            </div>
          ) : camsGapModal.rows.length === 0 ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="cams-gap-empty">
              ✓ No unresolved folios for this period — every CAMS row already resolves via the PAN/name chain.
            </div>
          ) : (
            <>
              <div className="mb-2 flex items-center gap-2 text-[11px] text-slate-500">
                <span data-testid="cams-gap-progress">
                  {camsGapModal.rows.filter((r) => r.chosen).length} / {camsGapModal.rows.length} tagged
                </span>
                <span className="text-slate-300">·</span>
                <span data-testid="cams-gap-selected-count">
                  {camsGapModal.rows.filter((r) => r.selected).length} selected
                </span>
              </div>

              <div className="max-h-[440px] overflow-auto rounded-md border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 z-10 bg-slate-50 text-[10px] uppercase text-slate-600">
                    <tr>
                      <th className="w-10 px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          data-testid="cams-gap-select-all"
                          checked={camsGapModal.rows.length > 0 && camsGapModal.rows.every((r) => r.selected)}
                          onChange={(e) => toggleAllCamsGapSelected(e.target.checked)}
                          title="Select / deselect all"
                        />
                      </th>
                      <th className="px-2 py-2 text-left">Investor</th>
                      <th className="px-2 py-2 text-left">Folio</th>
                      <th className="px-2 py-2 text-left">PAN</th>
                      <th className="px-2 py-2 text-right">Brokerage (₹)</th>
                      <th className="px-2 py-2 text-left">Reason</th>
                      <th className="px-2 py-2 text-left w-56">SubBroker (or bulk‑apply below)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {camsGapModal.rows.map((r, i) => (
                      <tr
                        key={r.folio_no || i}
                        className={`border-t border-slate-100 ${r.selected ? "bg-amber-50/40" : ""} cursor-pointer hover:bg-slate-50/60`}
                        onClick={() => toggleCamsGapSelected(r.folio_no)}
                      >
                        <td className="px-2 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            data-testid={`cams-gap-check-${i}`}
                            checked={!!r.selected}
                            onChange={() => toggleCamsGapSelected(r.folio_no)}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-slate-800">
                          {r.investor_name || "—"}
                          {r.sample_scheme && (
                            <div className="text-[10px] text-slate-400 truncate max-w-[240px]" title={r.sample_scheme}>
                              {r.sample_scheme}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[11px] text-slate-600">{r.folio_no}</td>
                        <td className="px-2 py-1.5 font-mono text-[11px] text-slate-500">{r.pan || "—"}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">
                          {Number(r.brkage_amt || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-2 py-1.5">
                          <Badge variant="outline" className="text-[10px] font-normal">
                            {r.reason === "folio_missing_from_investor_master" ? "folio not in IM"
                              : r.reason === "folio_has_no_pan" ? "IM row has no PAN"
                              : r.reason === "pan_not_in_subbroker_mapping" ? "PAN not mapped"
                              : r.reason}
                          </Badge>
                        </td>
                        <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                          <select
                            value={r.chosen || ""}
                            onChange={(e) => setCamsGapRow(r.folio_no, e.target.value)}
                            data-testid={`cams-gap-select-${i}`}
                            className={`h-7 w-full rounded-md border bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500 ${r.chosen ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200"}`}
                          >
                            <option value="">— select SubBroker —</option>
                            {camsGapModal.options.map((o) => (
                              <option key={o.code} value={o.code}>
                                {o.code}{o.name ? ` — ${o.name}` : ""}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Bulk-apply bar — moved below the table so the operator ticks
                  rows first, then picks a SubBroker here to tag them all. */}
              <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs">
                <span className="font-semibold text-amber-900">
                  Bulk‑tag {camsGapModal.rows.filter((r) => r.selected).length > 0
                    ? `${camsGapModal.rows.filter((r) => r.selected).length} selected row(s)`
                    : "still‑blank rows"} to:
                </span>
                <select
                  value={camsGapModal.bulk}
                  onChange={(e) => setCamsGapModal((p) => ({ ...p, bulk: e.target.value }))}
                  data-testid="cams-gap-bulk-select"
                  className="h-8 flex-1 min-w-[240px] rounded-md border border-amber-300 bg-white px-2 text-xs"
                >
                  <option value="">— pick SubBroker —</option>
                  {camsGapModal.options.map((o) => (
                    <option key={o.code} value={o.code}>
                      {o.code}{o.name ? ` — ${o.name}` : ""}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={applyCamsGapBulk}
                  disabled={!camsGapModal.bulk}
                  data-testid="cams-gap-bulk-apply"
                  className="h-8 bg-amber-600 hover:bg-amber-700 text-white text-xs"
                >
                  Apply
                </Button>
                {camsGapModal.rows.some((r) => r.selected) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleAllCamsGapSelected(false)}
                    data-testid="cams-gap-clear-selection"
                    className="h-8 text-xs"
                  >
                    Clear selection
                  </Button>
                )}
              </div>
            </>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCamsGapModal({ open: false, rows: [], options: [], loading: false, bulk: "" })}
              disabled={camsGapSaving}
              data-testid="cams-gap-cancel"
            >
              Cancel
            </Button>
            {camsGapModal.rows.length > 0 && (
              <Button
                onClick={saveCamsGapMappings}
                disabled={camsGapSaving || camsGapModal.rows.filter((r) => r.chosen).length === 0}
                data-testid="cams-gap-save"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {camsGapSaving ? (
                  <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Saving…</>
                ) : (
                  <>Save {camsGapModal.rows.filter((r) => r.chosen).length} tagged & rebuild cache</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
}

function AdminCleanupCard({ runs, busy, onCleaned }) {
  // Distinct periods available, sourced from Recent runs. Fallback to current
  // / previous month if no runs exist yet.
  const periodsFromRuns = Array.from(
    new Set((runs || []).map((r) => r.period).filter(Boolean))
  ).sort().reverse();
  const fallback = (() => {
    const _d = defaultPeriodMonthYear();
    return [periodString(_d.month, _d.year)];
  })();
  const options = periodsFromRuns.length ? periodsFromRuns : fallback;
  const [target, setTarget] = useState(options[0] || "");
  const [working, setWorking] = useState(false);
  useEffect(() => {
    if (!target && options.length) setTarget(options[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.length]);

  const doCleanup = async () => {
    if (!target) {
      toast.error("Pick a period to clean up.");
      return;
    }
    const proceed = window.confirm(
      `Permanently delete ALL commission artefacts (runs, ZIPs, raw rows) for period ${periodLabel(target)} (${target})?\n\nThis cannot be undone.`
    );
    if (!proceed) return;
    setWorking(true);
    try {
      const r = await axios.post(
        `${API}/commissions/admin-cleanup-period`,
        { period: target, confirm: "DELETE" },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      const d = r.data?.deleted || {};
      const total = Object.values(d).reduce((a, b) => a + (Number(b) || 0), 0);
      toast.success(`Cleaned ${total} doc(s) for ${periodLabel(target)} — see console for breakdown.`);
      // eslint-disable-next-line no-console
      console.info(`[Commissions cleanup] period=${target}`, d);
      onCleaned && onCleaned();
    } catch (e) {
      toast.error(`Cleanup failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setWorking(false);
    }
  };

  return (
    <Card className="mt-6 border-rose-200" data-testid="commissions-admin-cleanup-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-rose-700">
          <Trash2 className="h-5 w-5" />
          Admin: clean up a commission period
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-slate-600">
          Use this only to wipe dummy / test data from a specific month. Removes
          <code className="mx-1">commission_runs</code>, GridFS ZIPs,
          <code className="mx-1">WBR77 / MFSD205 / VOLTMONEY</code> rows, chunks, and job state for that period.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            disabled={busy || working || options.length === 0}
            data-testid="commissions-cleanup-period-select"
            className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900"
          >
            {options.map((p) => (
              <option key={p} value={p}>
                {periodLabel(p)} ({p})
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            onClick={doCleanup}
            disabled={busy || working || !target}
            data-testid="commissions-cleanup-btn"
          >
            {working ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Cleaning…
              </>
            ) : (
              <>
                <Trash2 className="mr-1 h-3.5 w-3.5" />
                Delete period
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Step 2 — Top-level card: pick a completed run, upload its invoices,
// review readiness, send emails. This is the primary entry-point for
// invoices/email (NOT buried inside Recent Runs).
// ──────────────────────────────────────────────────────────────────────
function Step2InvoiceAndEmail({ runs, busy, uploadMode = "auto" }) {
  // Only runs whose ZIP is still available (i.e., GridFS within 7-day TTL)
  // are eligible for invoice attach + email — you can't email what you
  // can't attach.
  const availableRuns = (runs || []).filter((r) => r.job_id && r.available);
  const [selectedJobId, setSelectedJobId] = useState("");
  // Bumped whenever upload / send inside a child panel changes state, so
  // both cards re-key and refetch to stay consistent.
  const [refreshTick, setRefreshTick] = useState(0);
  // Auto flow is 5-step (Watchlist, Prepare, Download ZIP, Upload, Send).
  // Manual flow is 3-step (Upload files, Upload invoices, Send).
  const stepNums = uploadMode === "manual"
    ? { upload: 2, send: 3 }
    : { upload: 4, send: 5 };

  // Default to most recent available run on first paint and any time runs
  // refresh from the parent (e.g. after a new commission ZIP completes).
  useEffect(() => {
    if (availableRuns.length === 0) {
      setSelectedJobId("");
      return;
    }
    const found = availableRuns.find((r) => r.job_id === selectedJobId);
    if (!found) setSelectedJobId(availableRuns[0].job_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableRuns.map((r) => r.job_id).join(",")]);

  const selectedRun = availableRuns.find((r) => r.job_id === selectedJobId);

  const RunPicker = () => (
    availableRuns.length > 0 && (
      <div className="mb-3 flex flex-wrap items-center gap-3" data-testid="step2-run-picker">
        <Label className="text-sm font-semibold text-slate-700">Run:</Label>
        <select
          value={selectedJobId}
          onChange={(e) => setSelectedJobId(e.target.value)}
          disabled={busy}
          className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900"
          data-testid="step2-run-select"
        >
          {availableRuns.map((r) => (
            <option key={r.job_id} value={r.job_id}>
              {(r.period || "—")} · {formatDateTime(r.run_at)}
              {r.user_name ? ` · ${r.user_name}` : ""}
            </option>
          ))}
        </select>
        {selectedRun && (
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
            {selectedRun.period}
          </Badge>
        )}
      </div>
    )
  );

  const EmptyState = ({ label }) => (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid={`step2-empty-${label}`}>
      <AlertTriangle className="inline h-4 w-4 mr-1 text-slate-400" />
      Generate a commission ZIP first (Step {uploadMode === "manual" ? 1 : 3}). This step unlocks once a run is available.
    </div>
  );

  return (
    <>
      {/* Upload invoices (Auto Step 4 / Manual Step 2) */}
      <Card className="mb-6 border-emerald-200" data-testid="commissions-step-upload-invoices-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white">Step {stepNums.upload}</Badge>
            <Upload className="h-5 w-5 text-emerald-700" />
            Upload invoices
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {availableRuns.length === 0 ? (
            <EmptyState label="upload" />
          ) : (
            <>
              <RunPicker />
              {selectedRun && (
                <InvoiceEmailPanel
                  run={selectedRun}
                  phase="upload"
                  key={`upload-${selectedRun.job_id}-${refreshTick}`}
                  onChanged={() => setRefreshTick((t) => t + 1)}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Send to sub-brokers (Auto Step 5 / Manual Step 3) */}
      <Card className="mb-6 border-emerald-200" data-testid="commissions-step-send-emails-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white">Step {stepNums.send}</Badge>
            <Mail className="h-5 w-5 text-emerald-700" />
            Send to sub-brokers
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {availableRuns.length === 0 ? (
            <EmptyState label="send" />
          ) : (
            <>
              <RunPicker />
              {selectedRun && (
                <InvoiceEmailPanel
                  run={selectedRun}
                  phase="send"
                  key={`send-${selectedRun.job_id}-${refreshTick}`}
                  onChanged={() => setRefreshTick((t) => t + 1)}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}


// ──────────────────────────────────────────────────────────────────────
// Invoice upload + bulk email panel — inline per-run section
// Shows a 2-column layout:
//   left: drag/drop multi-PDF upload + uploaded invoices list (per code)
//   right: send-emails plan (per-sub-broker readiness) + Send button
// ──────────────────────────────────────────────────────────────────────
function InvoiceEmailPanel({ run, phase = "both", onChanged }) {
  const jobId = run.job_id;
  const period = run.period || "";
  const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
  const showUpload = phase === "upload" || phase === "both";
  const showSend = phase === "send" || phase === "both";

  const [invoices, setInvoices] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [selectedCodes, setSelectedCodes] = useState(() => new Set());
  const fileInputRef = useRef(null);

  const refresh = async () => {
    try {
      const [invR, planR] = await Promise.all([
        axios.get(`${API}/commissions/invoices/${jobId}`, { headers: authHeader }),
        axios.post(
          `${API}/commissions/send-emails`,
          { job_id: jobId, dry_run: true },
          { headers: { ...authHeader, "Content-Type": "application/json" } }
        ),
      ]);
      setInvoices(invR.data?.invoices || []);
      const newPlan = planR.data || null;
      setPlan(newPlan);
      // Auto-select every freshly-ready code so the default "Send" still
      // matches the prior behaviour (send to all). User can uncheck to
      // exclude individual sub-brokers without deleting their invoice.
      if (newPlan?.eligible) {
        setSelectedCodes((prev) => {
          const next = new Set(prev);
          // Add any newly-ready codes
          for (const c of newPlan.eligible) next.add(c);
          // Drop any codes that are no longer ready (invoice deleted etc.)
          const eligibleSet = new Set(newPlan.eligible);
          for (const c of Array.from(next)) {
            if (!eligibleSet.has(c)) next.delete(c);
          }
          return next;
        });
      }
    } catch (e) {
      toast.error(`Failed to load: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoadingPlan(false);
    }
  };

  useEffect(() => {
    setLoadingPlan(true);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const handleUpload = async (files) => {
    if (!files || !files.length) return;
    const fd = new FormData();
    fd.append("job_id", jobId);
    if (period) fd.append("period", period);
    for (const f of files) fd.append("files", f);
    setUploading(true);
    try {
      // NOTE: do NOT set Content-Type manually for FormData uploads.
      // axios 1.x will respect the override and send `multipart/form-data`
      // WITHOUT the boundary marker, which makes FastAPI / the gateway
      // unable to parse the body and the browser shows "Network Error".
      // Letting axios omit the header lets it auto-generate
      // `multipart/form-data; boundary=...` correctly.
      const r = await axios.post(`${API}/commissions/upload-invoice`, fd, {
        headers: { ...authHeader },
      });
      const results = r.data?.results || [];
      const ok = results.filter((x) => x.status === "uploaded").length;
      const bad = results.filter((x) => x.status !== "uploaded");
      if (ok) toast.success(`Uploaded ${ok} invoice(s).`);
      if (bad.length) {
        toast.error(
          `${bad.length} file(s) skipped: ${bad
            .slice(0, 3)
            .map((x) => `${x.filename} (${x.error || x.status})`)
            .join("; ")}`
        );
      }
      await refresh();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(`Upload failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteInvoice = async (fileId) => {
    if (!window.confirm("Delete this invoice from the run?")) return;
    try {
      await axios.delete(`${API}/commissions/invoices/${fileId}`, { headers: authHeader });
      toast.success("Invoice removed.");
      await refresh();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const handleSend = async () => {
    const toSend = Array.from(selectedCodes);
    if (!toSend.length) {
      toast.error("Pick at least one sub-broker to email.");
      return;
    }
    const proceed = window.confirm(
      `Send brokerage payout emails to ${toSend.length} selected sub-broker(s) for ${period}?\n\n` +
        `Selected: ${toSend.join(", ")}\n\n` +
        `Sub-brokers without an uploaded invoice or registered email are not selectable.`
    );
    if (!proceed) return;
    setSending(true);
    setSendResult(null);
    try {
      const r = await axios.post(
        `${API}/commissions/send-emails`,
        { job_id: jobId, dry_run: false, only_codes: toSend },
        { headers: { ...authHeader, "Content-Type": "application/json" } }
      );
      const d = r.data || {};
      setSendResult(d);
      toast.success(
        `Sent ${d.sent_count}/${toSend.length}.` +
          (d.failed_count ? ` ${d.failed_count} failed.` : "")
      );
      await refresh();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(`Send failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleTestSend = async () => {
    if (!testEmail || !testEmail.includes("@")) {
      toast.error("Enter a valid email for the test send.");
      return;
    }
    if (!plan?.eligible?.length) {
      toast.error("Need at least one ready sub-broker (invoice + email) for the test.");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/commissions/send-emails`,
        { job_id: jobId, test_to: testEmail.trim() },
        { headers: { ...authHeader, "Content-Type": "application/json" } }
      );
      toast.success(
        `Test email sent to ${testEmail} (using ${r.data?.sub_broker_code_used} files).`
      );
    } catch (e) {
      toast.error(`Test send failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const planRows = plan?.plan || [];
  const readyCount = plan?.ready || 0;
  const totalCodes = plan?.total || 0;

  return (
    <div data-testid="invoice-email-panel">
      {showUpload && (
        <div className="mb-3">
          <p className="text-xs text-slate-600">
            Upload one PDF per sub-broker (filename pattern{" "}
            <code className="rounded bg-slate-100 px-1">..._&lt;sub-broker-code&gt;.pdf</code>
            , e.g. <code className="rounded bg-slate-100 px-1">2026_05_31_1994103.pdf</code>).
          </p>
        </div>
      )}
      {showSend && phase === "send" && (
        <div className="mb-3">
          <p className="text-xs text-slate-600">
            Emails go from{" "}
            <span className="font-medium">info@kinntegra.co.in</span> only to sub-brokers who have BOTH an
            invoice uploaded AND a registered email — others are skipped.
          </p>
        </div>
      )}

      <div className={phase === "both" ? "grid gap-4 lg:grid-cols-2" : ""}>
        {showUpload && (
        /* LEFT — upload */
        <div className="rounded-lg border border-amber-200 bg-white p-4">
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            Upload invoice PDFs
          </Label>
          <div
            className="mt-2 flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-amber-300 bg-amber-50/40 p-6 text-center"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleUpload(Array.from(e.dataTransfer.files || []).filter((f) =>
                f.name.toLowerCase().endsWith(".pdf")
              ));
            }}
            data-testid="invoice-dropzone"
          >
            <FileText className="h-7 w-7 text-amber-500" />
            <p className="text-xs text-slate-600">
              Drag & drop PDFs here, or click below to select multiple files.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              multiple
              className="hidden"
              onChange={(e) => handleUpload(Array.from(e.target.files || []))}
              data-testid="invoice-file-input"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="invoice-pick-files-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <Upload className="mr-1 h-3.5 w-3.5" />
                  Select PDFs
                </>
              )}
            </Button>
          </div>

          <div className="mt-3">
            <p className="mb-1 text-xs font-semibold text-slate-700">
              Uploaded invoices ({invoices.length})
            </p>
            {invoices.length === 0 ? (
              <p className="text-xs italic text-slate-400">No invoices uploaded yet.</p>
            ) : (
              <ul className="max-h-48 overflow-y-auto rounded border border-slate-100 divide-y divide-slate-100">
                {invoices.map((inv) => (
                  <li
                    key={inv.file_id}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 text-xs hover:bg-slate-50"
                    data-testid={`invoice-row-${inv.sub_broker_code}`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800">{inv.sub_broker_code}</div>
                      <div className="truncate text-[11px] text-slate-500" title={inv.filename}>
                        {inv.filename} · {formatBytes(inv.size_bytes)}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteInvoice(inv.file_id)}
                      className="rounded p-1 text-rose-500 hover:bg-rose-50"
                      title="Remove invoice"
                      data-testid={`invoice-delete-${inv.sub_broker_code}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        )}

        {showSend && (
        /* RIGHT — readiness + send */
        <div className="rounded-lg border border-emerald-200 bg-white p-4">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Send plan
            </Label>
            <Badge
              variant="outline"
              className="bg-emerald-50 text-emerald-700 border-emerald-200"
              data-testid="invoice-ready-badge"
            >
              {readyCount}/{totalCodes} ready
            </Badge>
          </div>

          {loadingPlan ? (
            <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading plan…
            </div>
          ) : (
            <div className="mt-2 max-h-64 overflow-y-auto rounded border border-slate-100">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="px-2 py-1 text-center w-8">
                      <input
                        type="checkbox"
                        title="Select / deselect all ready sub-brokers"
                        data-testid="invoice-select-all"
                        checked={
                          (plan?.eligible || []).length > 0 &&
                          (plan?.eligible || []).every((c) => selectedCodes.has(c))
                        }
                        onChange={(e) => {
                          const all = new Set(plan?.eligible || []);
                          setSelectedCodes(e.target.checked ? all : new Set());
                        }}
                      />
                    </th>
                    <th className="px-2 py-1 text-left">Sub-broker</th>
                    <th className="px-2 py-1 text-left">Email</th>
                    <th className="px-2 py-1">Inv</th>
                    <th className="px-2 py-1">Email</th>
                    <th className="px-2 py-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {planRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-2 py-3 text-center italic text-slate-400">
                        No sub-brokers found in this run's ZIP.
                      </td>
                    </tr>
                  ) : (
                    planRows.map((p) => {
                      const isChecked = selectedCodes.has(p.sub_broker_code);
                      const toggle = () => {
                        setSelectedCodes((prev) => {
                          const next = new Set(prev);
                          if (next.has(p.sub_broker_code)) next.delete(p.sub_broker_code);
                          else next.add(p.sub_broker_code);
                          return next;
                        });
                      };
                      return (
                      <tr
                        key={p.sub_broker_code}
                        className={`border-t border-slate-100 ${p.ready ? "cursor-pointer hover:bg-emerald-50/40" : ""}`}
                        data-testid={`plan-row-${p.sub_broker_code}`}
                        onClick={p.ready ? toggle : undefined}
                      >
                        <td className="px-2 py-1 text-center">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={toggle}
                            onClick={(e) => e.stopPropagation()}
                            disabled={!p.ready}
                            data-testid={`plan-check-${p.sub_broker_code}`}
                            title={p.ready ? "Include in send" : "Cannot send — missing invoice or email"}
                          />
                        </td>
                        <td className="px-2 py-1 font-medium text-slate-800">
                          {p.sub_broker_code}
                          {p.name && (
                            <div className="text-[10px] text-slate-400">{p.name}</div>
                          )}
                        </td>
                        <td className="px-2 py-1 text-[11px] text-slate-600">
                          {p.email || <span className="italic text-slate-400">—</span>}
                        </td>
                        <td className="px-2 py-1 text-center">
                          {p.has_invoice ? "✓" : "—"}
                        </td>
                        <td className="px-2 py-1 text-center">
                          {p.has_email ? "✓" : "—"}
                        </td>
                        <td className="px-2 py-1 text-center">
                          {p.ready ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border-0">
                              READY
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-500">
                              SKIP
                            </Badge>
                          )}
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2">
              <Input
                type="email"
                placeholder="Test send to (your email)…"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                disabled={sending}
                data-testid="invoice-test-email-input"
                className="h-8 text-xs"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleTestSend}
                disabled={sending || readyCount === 0}
                data-testid="invoice-test-send-btn"
              >
                <Send className="mr-1 h-3 w-3" />
                Test
              </Button>
            </div>
            <Button
              onClick={handleSend}
              disabled={sending || selectedCodes.size === 0}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="invoice-send-all-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send to {selectedCodes.size} selected sub-broker{selectedCodes.size === 1 ? "" : "s"}
                </>
              )}
            </Button>
            <p className="text-[10px] text-slate-500">
              Tick the checkboxes next to each READY sub-broker to include them. Sub-brokers without an
              uploaded invoice or registered email cannot be selected. {readyCount > 0 && (
                <span className="font-medium text-slate-700">
                  {selectedCodes.size}/{readyCount} ready sub-brokers selected.
                </span>
              )}
            </p>
          </div>

          {sendResult && (
            <div
              className="mt-3 rounded border border-emerald-200 bg-emerald-50 p-2 text-xs"
              data-testid="invoice-send-result"
            >
              <div className="font-semibold text-emerald-900">
                Sent: {sendResult.sent_count} · Failed: {sendResult.failed_count} · Skipped:{" "}
                {sendResult.skipped_count}
              </div>
              {(sendResult.sent || []).filter((s) => s.attached_as === "zip").length > 0 && (
                <div className="mt-1 text-amber-700">
                  📦 Auto-zipped {(sendResult.sent || []).filter((s) => s.attached_as === "zip").length} oversized
                  attachment{(sendResult.sent || []).filter((s) => s.attached_as === "zip").length === 1 ? "" : "s"}{": "}
                  {(sendResult.sent || [])
                    .filter((s) => s.attached_as === "zip")
                    .map((s) => s.sub_broker_code)
                    .join(", ")}
                </div>
              )}
              {(sendResult.sent || []).filter((s) => s.attached_as === "link").length > 0 && (
                <div className="mt-1 text-sky-700">
                  🔗 Sent as download link (file too large for direct attach):{" "}
                  {(sendResult.sent || [])
                    .filter((s) => s.attached_as === "link")
                    .map((s) => s.sub_broker_code)
                    .join(", ")}
                </div>
              )}
              {(sendResult.failed || []).map((f) => (
                <div key={f.sub_broker_code} className="text-rose-700">
                  ✗ {f.sub_broker_code}: {f.error}
                </div>
              ))}
            </div>
          )}

        </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Exclusions tab — standalone module that filters (PAN × sub_broker_code)
// rows out of raw WBR77 / MFSD205 Excel files on demand. The live
// WBR77 / MFSD205 collections and all dashboards remain untouched.
function ExclusionsTab() {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
  const [list, setList] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [addOpen, setAddOpen] = React.useState(false);
  const [period, setPeriod] = React.useState("");
  const [uploading, setUploading] = React.useState("");
  // Live-progress state for sequential bulk uploads.
  // Each item: {name, size, type: 'wbr77'|'mfsd205'|'unknown',
  //              status: 'queued'|'uploading'|'done'|'failed',
  //              removed, total, error, startedAt, finishedAt, blob}
  const [batch, setBatch] = React.useState([]);
  const [batchActiveIdx, setBatchActiveIdx] = React.useState(-1);
  const batchCancelRef = React.useRef(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/commission-exclusions/list`, auth());
      setList(r.data?.exclusions || []);
    } catch (e) {
      toast.error("Failed to load exclusions");
    } finally {
      setLoading(false);
    }
  };
  React.useEffect(() => { fetchList(); }, []);

  const onToggle = async (id) => {
    try {
      await axios.patch(`${API}/commission-exclusions/${id}/toggle`, {}, auth());
      fetchList();
    } catch { toast.error("Toggle failed"); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Remove this exclusion?")) return;
    try {
      await axios.delete(`${API}/commission-exclusions/${id}`, auth());
      fetchList();
      toast.success("Exclusion removed");
    } catch { toast.error("Delete failed"); }
  };

  const onSubBrokerUpload = async (file) => {
    if (!file) return;
    setUploading("subbroker");
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Server infers password from filename; we don't ask the user.
      const r = await axios.post(`${API}/commission-exclusions/filter-subbroker-xlsx`, fd, {
        headers: { ...auth().headers },
        responseType: "blob",
        timeout: 5 * 60 * 1000,
      });
      const total = r.headers["x-rows-total"];
      const removed = r.headers["x-rows-removed"];
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      // Keep the filename identical to the input (no "_filtered" suffix).
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Filtered per-sub-broker file: ${removed}/${total} rows removed`);
    } catch (e) {
      let detail = e.message;
      if (e.response?.data instanceof Blob) {
        try { detail = await e.response.data.text(); } catch { /* ignore */ }
      } else if (e.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      toast.error(`Filter failed: ${detail}`);
    } finally {
      setUploading("");
    }
  };

  const onFileUpload = async (which, file) => {
    if (!file) return;
    setUploading(which);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (period) fd.append("period", period);
      const r = await axios.post(`${API}/commission-exclusions/filter-${which}`, fd, {
        headers: { ...auth().headers },
        responseType: "blob",
        timeout: 10 * 60 * 1000,
      });
      // Default row counts to 0 so an interrupted stream never shows
      // "NaN/NaN" in the toast. Streaming CSV responses set the header
      // literal "streaming" and emit the counts in a trailer line.
      let total = 0;
      let removed = 0;
      const hTotal = r.headers["x-rows-total"];
      const hRemoved = r.headers["x-rows-removed"];
      if (hTotal && hTotal !== "streaming" && Number.isFinite(Number(hTotal))) total = Number(hTotal);
      if (hRemoved && hRemoved !== "streaming" && Number.isFinite(Number(hRemoved))) removed = Number(hRemoved);
      if (hTotal === "streaming") {
        try {
          const tailBytes = Math.min(r.data.size, 400);
          const tail = await r.data.slice(r.data.size - tailBytes).text();
          const m = tail.match(/### STATS: rows_before=(\d+),rows_removed=(\d+) ###/);
          if (m) { total = Number(m[1]); removed = Number(m[2]); }
        } catch { /* ignore */ }
      }
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${which.toUpperCase()}_filtered${period ? "_" + period : ""}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Filtered ${which.toUpperCase()}: ${removed}/${total} rows removed`);
    } catch (e) {
      // Blob error responses need to be read as text first.
      let detail = e.message;
      if (e.response?.data instanceof Blob) {
        try { detail = await e.response.data.text(); } catch {}
      } else if (e.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      toast.error(`Filter failed: ${detail}`);
    } finally {
      setUploading("");
    }
  };

  // Infer whether a file is WBR77 (CAMS) or MFSD205 (Karvy) from its name.
  // Bulk mode supports both — user can mix 11 CAMS + 11 Karvy in one go.
  const detectType = (fname) => {
    const u = (fname || "").toUpperCase();
    if (u.includes("WBR") || u.includes("CAMS")) return "wbr77";
    if (u.includes("MFSD") || u.includes("KARVY")) return "mfsd205";
    return "unknown";
  };

  // Per-sub-broker files always go through the subbroker endpoint (which
  // decrypts, filters all sheets, and returns an xlsx). The bulk-upload
  // sequential loop routes files tagged `subbroker` here instead of the
  // CSV filter endpoints.
  const SUBBROKER_TYPE = "subbroker";

  const cancelBatch = () => {
    batchCancelRef.current = true;
  };

  const onBulkUpload = async (fileList, forcedType) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    // Build initial batch state. If a type is forced (CAMS-only / Karvy-only
    // button) every file is tagged that way; otherwise auto-detect per file.
    const initial = files.map((f) => ({
      name: f.name,
      size: f.size,
      type: forcedType || detectType(f.name),
      status: "queued",
      removed: null,
      total: null,
      error: null,
      // IMPORTANT: we no longer hold the Blob after download. 22 × 112 MB
      // CSVs OOMs the Chrome tab, which disconnects mid-stream and the
      // backend sees it as a cancelled request — the user then gets a
      // truncated file (we observed 42k rows out of 179k on file 3).
      // Each CSV is auto-downloaded immediately when it finishes and
      // the blob reference is released. `downloaded: true` marks it.
      downloaded: false,
      startedAt: null,
      finishedAt: null,
    }));
    setBatch(initial);
    setBatchActiveIdx(-1);
    setUploading("bulk");
    batchCancelRef.current = false;
    const results = [...initial];
    for (let i = 0; i < files.length; i++) {
      if (batchCancelRef.current) {
        for (let k = i; k < results.length; k++) {
          if (results[k].status === "queued") {
            results[k] = { ...results[k], status: "failed", error: "Cancelled" };
          }
        }
        setBatch([...results]);
        break;
      }
      setBatchActiveIdx(i);
      results[i] = { ...results[i], status: "uploading", startedAt: Date.now() };
      setBatch([...results]);
      const file = files[i];
      const type = results[i].type === "unknown" ? "wbr77" : results[i].type;
      // Pick endpoint per type: subbroker XLSX files use their own
      // decrypt+filter route; raw CAMS/Karvy use the streaming CSV routes.
      const endpoint = type === SUBBROKER_TYPE
        ? `${API}/commission-exclusions/filter-subbroker-xlsx`
        : `${API}/commission-exclusions/filter-${type}`;
      // Retry transient failures up to 2 extra times. Preview/LIVE sit
      // behind Cloudflare, which returns 502 "origin_bad_gateway" when a
      // slow origin response takes longer than the edge timeout. This is
      // not a code bug — the backend logs still show 200 OK. Auto-retry
      // smooths over these.
      const MAX_ATTEMPTS = 3;
      let lastError = null;
      let success = false;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS && !batchCancelRef.current; attempt++) {
        if (attempt > 1) {
          results[i] = { ...results[i], status: "uploading", error: `Retrying (${attempt}/${MAX_ATTEMPTS}) — ${lastError?.slice(0, 80) || ""}` };
          setBatch([...results]);
          await new Promise((res) => setTimeout(res, 10000 * (attempt - 1)));
        }
        try {
          const fd = new FormData();
          fd.append("file", file);
          if (period) fd.append("period", period);
          const r = await axios.post(endpoint, fd, {
            headers: { ...auth().headers },
            responseType: "blob",
            timeout: 10 * 60 * 1000,
          });
          // Row-count headers are only present on non-streaming (XLSX)
          // responses. For streaming CSV, parse the trailing
          // `### STATS: rows_before=X,rows_removed=Y ###` line that the
          // backend emits as its final chunk.
          let total = 0;
          let removed = 0;
          const hTotal = r.headers["x-rows-total"];
          const hRemoved = r.headers["x-rows-removed"];
          if (hTotal && hTotal !== "streaming" && Number.isFinite(Number(hTotal))) total = Number(hTotal);
          if (hRemoved && hRemoved !== "streaming" && Number.isFinite(Number(hRemoved))) removed = Number(hRemoved);
          if (hTotal === "streaming") {
            try {
              const tailBytes = Math.min(r.data.size, 400);
              const tail = await r.data.slice(r.data.size - tailBytes).text();
              const m = tail.match(/### STATS: rows_before=(\d+),rows_removed=(\d+) ###/);
              if (m) { total = Number(m[1]); removed = Number(m[2]); }
            } catch { /* ignore */ }
          }
          // For sub-broker bulk, we KEEP each blob and build a single
          // ZIP at the end — files are small (~1-2 MB each) so 22 of
          // them fit in RAM safely. For raw CAMS/Karvy bulk (112 MB
          // outputs), we download+release each immediately to avoid
          // Chrome OOM. Filename is kept identical to input (no
          // "_filtered" suffix).
          const downloadName = file.name;
          if (type === SUBBROKER_TYPE) {
            results[i] = {
              ...results[i],
              status: "done",
              removed,
              total,
              downloaded: true,
              blob: r.data,
              blobName: downloadName,
              finishedAt: Date.now(),
              error: null,
            };
          } else {
            try {
              const url = window.URL.createObjectURL(r.data);
              const a = document.createElement("a");
              a.href = url;
              a.download = downloadName;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
            } catch (dlErr) {
              console.warn(`[bulk ${i + 1}/${files.length}] download trigger failed (file was still filtered successfully):`, dlErr);
            }
            results[i] = {
              ...results[i],
              status: "done",
              removed,
              total,
              downloaded: true,
              finishedAt: Date.now(),
              error: null,
            };
          }
          success = true;
          break;
        } catch (e) {
          let detail = e.message || "Unknown error";
          if (e.response?.data instanceof Blob) {
            try { detail = await e.response.data.text(); } catch { /* ignore */ }
          } else if (e.response?.data?.detail) {
            detail = e.response.data.detail;
          }
          const status = e.response?.status;
          const code = e.code || "";
          const isTransient =
            status === 502 ||
            status === 503 ||
            status === 504 ||
            code === "ECONNABORTED" ||
            code === "ERR_NETWORK" ||
            /network error/i.test(detail);
          const parts = [];
          if (code) parts.push(`code=${code}`);
          if (status) parts.push(`http=${status}`);
          parts.push(detail);
          lastError = parts.join(" · ");
          console.error(`[bulk ${i + 1}/${files.length}] attempt ${attempt}/${MAX_ATTEMPTS} failed for ${file.name}:`, e, {
            code, status, detail: lastError, transient: isTransient,
          });
          if (!isTransient) break;
        }
      }
      if (!success) {
        results[i] = {
          ...results[i],
          status: "failed",
          error: lastError || "Failed",
          finishedAt: Date.now(),
        };
      }
      setBatch([...results]);
    }
    setBatchActiveIdx(-1);
    const successes = results.filter((r) => r.status === "done");
    const totalRemoved = successes.reduce((s, r) => s + (r.removed || 0), 0);
    const failed = results.filter((r) => r.status === "failed").length;
    // Sub-broker bulk → pack every successful blob into a single ZIP
    // (small per-file size makes this safe). Raw CAMS/Karvy bulk →
    // each CSV was already auto-downloaded individually.
    const subbrokerBlobs = successes.filter((r) => r.blob && r.blobName);
    if (subbrokerBlobs.length > 0) {
      try {
        const zip = new JSZip();
        for (const r of subbrokerBlobs) {
          zip.file(r.blobName, r.blob);
        }
        const zipBlob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
        const url = window.URL.createObjectURL(zipBlob);
        const a = document.createElement("a");
        const ts = new Date().toISOString().slice(0, 10);
        a.href = url;
        a.download = `commission_exclusions_bulk_${ts}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (zErr) {
        toast.error(`Failed to build ZIP: ${zErr.message}`);
      }
    }
    if (successes.length > 0) {
      const kind = subbrokerBlobs.length > 0 ? "ZIP ready" : "done";
      toast.success(`Bulk ${kind} · ${successes.length}/${files.length} filtered · ${totalRemoved} rows removed${failed ? ` · ${failed} failed` : ""}`);
    } else {
      toast.error(`All ${files.length} files failed`);
    }
    setUploading("");
  };

  const retryFile = async (idx) => {
    const row = batch[idx];
    if (!row || row.status === "uploading") return;
    const results = [...batch];
    results[idx] = { ...row, status: "uploading", error: null, startedAt: Date.now() };
    setBatch(results);
    setBatchActiveIdx(idx);
    const type = row.type === "unknown" ? "wbr77" : row.type;
    try {
      // Re-upload requires the original File object, which we don't retain —
      // ask the user to re-pick that specific file.
      toast.info("Please re-select this file to retry.");
      results[idx] = { ...row, status: "failed", error: "Please re-upload this file" };
    } catch (e) {
      results[idx] = { ...row, status: "failed", error: String(e.message || e) };
    }
    setBatch(results);
    setBatchActiveIdx(-1);
  };

  const downloadOne = (idx) => {
    // Downloaded on-the-fly during the batch; we no longer keep the blob
    // in memory. If the user wants to retrieve a file after the batch,
    // they must re-upload (so we don't hold 22 × 112 MB in RAM).
    const row = batch[idx];
    if (!row) return;
    toast.info(`${row.name} was auto-downloaded. To get it again, re-upload this one file.`);
  };

  return (
    <div className="space-y-6" data-testid="exclusions-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2 text-lg">
            <span className="flex items-center gap-2">
              <X className="h-5 w-5 text-rose-600" />
              Excluded Clients
              <Badge variant="outline" className="ml-2 bg-slate-50 text-slate-600 border-slate-200 font-normal">
                {list.filter((x) => x.active).length} active · {list.length} total
              </Badge>
            </span>
            <Button size="sm" onClick={() => setAddOpen(true)} data-testid="exclusions-add-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Exclude a client
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          ) : list.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              No exclusions yet. Click <b>Exclude a client</b> above to add one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-2 text-left">PAN</th>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Sub-broker</th>
                    <th className="px-3 py-2 text-left">Reason</th>
                    <th className="px-3 py-2 text-left">Added on</th>
                    <th className="px-3 py-2 text-center">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((x) => (
                    <tr key={x.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`exclusion-row-${x.pan}`}>
                      <td className="px-3 py-2 font-mono text-slate-800">{x.pan}</td>
                      <td className="px-3 py-2 text-slate-700">{x.name || "—"}</td>
                      <td className="px-3 py-2 font-mono text-slate-700">{x.sub_broker_code}</td>
                      <td className="px-3 py-2 text-slate-500 italic">{x.reason || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {x.created_at ? new Date(x.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => onToggle(x.id)}
                          className="inline-flex items-center gap-1 text-xs font-medium"
                          data-testid={`exclusion-toggle-${x.pan}`}
                        >
                          {x.active ? (
                            <><ToggleRight className="h-5 w-5 text-emerald-600" /> <span className="text-emerald-700">Active</span></>
                          ) : (
                            <><ToggleLeft className="h-5 w-5 text-slate-400" /> <span className="text-slate-500">Paused</span></>
                          )}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => onDelete(x.id)}>
                          <Trash2 className="h-4 w-4 text-rose-600" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileSpreadsheet className="h-5 w-5 text-teal-600" />
            Filter a raw commission file
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-slate-600 leading-relaxed">
            Upload a raw <b>WBR77</b> (CAMS) or <b>MFSD205</b> (Karvy) Excel. The server will strip every row whose folio belongs to an actively-excluded PAN and whose sub-broker column equals the excluded code, then stream back the filtered file. The live commission database is <b>not</b> modified.
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="exclusion-period" className="text-sm font-medium whitespace-nowrap">Period (optional):</Label>
            <Input
              id="exclusion-period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="e.g. 202609"
              className="max-w-[180px]"
              data-testid="exclusion-period-input"
            />
            <span className="text-xs text-slate-500">Only used in the output filename.</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="relative flex flex-col gap-2 rounded-lg border-2 border-dashed border-fuchsia-400 bg-fuchsia-50 p-4 cursor-pointer hover:bg-fuchsia-100 transition">
              <div className="flex items-center gap-2 text-sm font-semibold text-fuchsia-700">
                <Upload className="h-4 w-4" /> Per-sub-broker Excel (single)
              </div>
              <div className="text-xs text-fuchsia-600">
                Decrypt with sub-broker code (from filename) · strips rows across every sheet
              </div>
              <input
                type="file"
                accept=".xlsx,.xlsm,.xls"
                className="absolute inset-0 opacity-0 cursor-pointer"
                disabled={uploading !== ""}
                onChange={(e) => onSubBrokerUpload(e.target.files?.[0])}
                data-testid="exclusion-upload-subbroker"
              />
              {uploading === "subbroker" && <Loader2 className="absolute top-3 right-3 h-4 w-4 animate-spin text-fuchsia-600" />}
            </label>
            <label className="relative flex flex-col gap-2 rounded-lg border-2 border-dashed border-fuchsia-500 bg-fuchsia-50 p-4 cursor-pointer hover:bg-fuchsia-100 transition">
              <div className="flex items-center gap-2 text-sm font-semibold text-fuchsia-700">
                <Upload className="h-4 w-4" /> Per-sub-broker Excel (bulk — up to 50 files)
              </div>
              <div className="text-xs text-fuchsia-600">
                Live progress · auto-decrypts each file by filename · each result downloads individually
              </div>
              <input
                type="file"
                accept=".xlsx,.xlsm,.xls"
                multiple
                className="absolute inset-0 opacity-0 cursor-pointer"
                disabled={uploading !== ""}
                onChange={(e) => onBulkUpload(e.target.files, "subbroker")}
                data-testid="exclusion-upload-bulk-subbroker"
              />
              {uploading === "bulk" && <Loader2 className="absolute top-3 right-3 h-4 w-4 animate-spin text-fuchsia-600" />}
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="relative flex flex-col gap-2 rounded-lg border-2 border-dashed border-sky-300 bg-sky-50 p-4 cursor-pointer hover:bg-sky-100 transition">
              <div className="flex items-center gap-2 text-sm font-semibold text-sky-700">
                <Upload className="h-4 w-4" /> Upload raw WBR77 (CAMS)
              </div>
              <div className="text-xs text-sky-600">Processes only the "Total" sheet · returns a CSV</div>
              <input
                type="file"
                accept=".xlsx,.xlsm,.xls"
                className="absolute inset-0 opacity-0 cursor-pointer"
                disabled={uploading !== ""}
                onChange={(e) => onFileUpload("wbr77", e.target.files?.[0])}
                data-testid="exclusion-upload-wbr77"
              />
              {uploading === "wbr77" && <Loader2 className="absolute top-3 right-3 h-4 w-4 animate-spin text-sky-600" />}
            </label>
            <label className="relative flex flex-col gap-2 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 p-4 cursor-pointer hover:bg-amber-100 transition">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-700">
                <Upload className="h-4 w-4" /> Upload raw MFSD205 (Karvy)
              </div>
              <div className="text-xs text-amber-600">Processes only the "Total" sheet · returns a CSV</div>
              <input
                type="file"
                accept=".xlsx,.xlsm,.xls"
                className="absolute inset-0 opacity-0 cursor-pointer"
                disabled={uploading !== ""}
                onChange={(e) => onFileUpload("mfsd205", e.target.files?.[0])}
                data-testid="exclusion-upload-mfsd205"
              />
              {uploading === "mfsd205" && <Loader2 className="absolute top-3 right-3 h-4 w-4 animate-spin text-amber-600" />}
            </label>
            <label className="relative flex flex-col gap-2 rounded-lg border-2 border-dashed border-emerald-400 bg-emerald-50 p-4 cursor-pointer hover:bg-emerald-100 transition">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
                <Upload className="h-4 w-4" /> Bulk raw CAMS + Karvy (mixed)
              </div>
              <div className="text-xs text-emerald-600">Auto-detect WBR77/MFSD205 · live progress · auto-download each</div>
              <input
                type="file"
                accept=".xlsx,.xlsm,.xls"
                multiple
                className="absolute inset-0 opacity-0 cursor-pointer"
                disabled={uploading !== ""}
                onChange={(e) => onBulkUpload(e.target.files)}
                data-testid="exclusion-upload-bulk"
              />
              {uploading === "bulk" && <Loader2 className="absolute top-3 right-3 h-4 w-4 animate-spin text-emerald-600" />}
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="relative flex flex-col gap-2 rounded-lg border-2 border-dashed border-sky-400 bg-sky-50 p-4 cursor-pointer hover:bg-sky-100 transition">
              <div className="flex items-center gap-2 text-sm font-semibold text-sky-700">
                <Upload className="h-4 w-4" /> Bulk — raw CAMS only (WBR77)
              </div>
              <div className="text-xs text-sky-600">All 22 months of raw CAMS · Total sheet only · live progress</div>
              <input
                type="file"
                accept=".xlsx,.xlsm,.xls"
                multiple
                className="absolute inset-0 opacity-0 cursor-pointer"
                disabled={uploading !== ""}
                onChange={(e) => onBulkUpload(e.target.files, "wbr77")}
                data-testid="exclusion-upload-bulk-cams"
              />
              {uploading === "bulk" && <Loader2 className="absolute top-3 right-3 h-4 w-4 animate-spin text-sky-600" />}
            </label>
            <label className="relative flex flex-col gap-2 rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 p-4 cursor-pointer hover:bg-amber-100 transition">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-700">
                <Upload className="h-4 w-4" /> Bulk — raw Karvy only (MFSD205)
              </div>
              <div className="text-xs text-amber-600">All 22 months of raw Karvy · Total sheet only · live progress</div>
              <input
                type="file"
                accept=".xlsx,.xlsm,.xls"
                multiple
                className="absolute inset-0 opacity-0 cursor-pointer"
                disabled={uploading !== ""}
                onChange={(e) => onBulkUpload(e.target.files, "mfsd205")}
                data-testid="exclusion-upload-bulk-karvy"
              />
              {uploading === "bulk" && <Loader2 className="absolute top-3 right-3 h-4 w-4 animate-spin text-amber-600" />}
            </label>
          </div>
          {uploading === "bulk" && batch.length > 0 && (
            <BulkProgress
              batch={batch}
              activeIdx={batchActiveIdx}
              onCancel={cancelBatch}
              onDownloadOne={downloadOne}
            />
          )}
          {uploading && uploading !== "bulk" && (
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Filtering… expect <b>~30-45 seconds per file</b> for 100MB WBR77/MFSD205 inputs. Browser will auto-download when ready.</span>
            </div>
          )}
          {uploading !== "bulk" && batch.length > 0 && (
            <BulkProgress
              batch={batch}
              activeIdx={-1}
              onCancel={null}
              onDownloadOne={downloadOne}
            />
          )}
        </CardContent>
      </Card>

      {addOpen && <AddExclusionDialog onClose={() => { setAddOpen(false); fetchList(); }} />}
    </div>
  );
}

// Live progress panel for sequential bulk uploads. Shows an overall progress
// bar + a per-file grid with status badges (Queued / Uploading / Done / Failed)
// so the user always knows exactly which of the 22 CAMS/Karvy files is being
// processed and can re-download any single filtered CSV without re-uploading.
function BulkProgress({ batch, activeIdx, onCancel, onDownloadOne }) {
  const total = batch.length;
  const done = batch.filter((r) => r.status === "done").length;
  const failed = batch.filter((r) => r.status === "failed").length;
  const pct = total > 0 ? Math.round(((done + failed) / total) * 100) : 0;
  const stillRunning = activeIdx >= 0;
  const fmtDur = (r) => {
    if (!r.startedAt) return "—";
    const end = r.finishedAt || Date.now();
    const s = Math.max(0, Math.round((end - r.startedAt) / 1000));
    return `${s}s`;
  };
  const activeName = stillRunning && batch[activeIdx] ? batch[activeIdx].name : null;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="bulk-progress-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-medium text-slate-700 flex items-center gap-2">
          {stillRunning ? (
            <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          )}
          <span>
            {done + failed} / {total} processed
            {failed > 0 && <span className="text-rose-600 ml-1">· {failed} failed</span>}
            {activeName && <span className="text-slate-500 ml-2">· Now: <b>{activeName}</b></span>}
          </span>
        </div>
        {stillRunning && onCancel && (
          <Button size="sm" variant="outline" onClick={onCancel} data-testid="bulk-cancel-btn">
            Cancel remaining
          </Button>
        )}
      </div>
      <Progress value={pct} className="h-2" data-testid="bulk-progress-bar" />
      <div className="max-h-64 overflow-y-auto rounded border border-slate-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="px-2 py-1.5 text-left w-8">#</th>
              <th className="px-2 py-1.5 text-left">File</th>
              <th className="px-2 py-1.5 text-left w-20">Type</th>
              <th className="px-2 py-1.5 text-right w-24">Removed</th>
              <th className="px-2 py-1.5 text-right w-20">Time</th>
              <th className="px-2 py-1.5 text-center w-24">Status</th>
              <th className="px-2 py-1.5 text-center w-20">Action</th>
            </tr>
          </thead>
          <tbody>
            {batch.map((r, i) => (
              <React.Fragment key={i}>
                <tr
                  className={`border-t border-slate-100 ${i === activeIdx ? "bg-emerald-50" : ""}`}
                  data-testid={`bulk-row-${i}`}
                >
                  <td className="px-2 py-1.5 text-slate-400">{i + 1}</td>
                  <td className="px-2 py-1.5 text-slate-700 truncate max-w-xs" title={r.name}>{r.name}</td>
                  <td className="px-2 py-1.5 text-slate-500 font-mono text-[11px]">{r.type}</td>
                  <td className="px-2 py-1.5 text-right text-slate-700 font-mono">
                    {r.status === "done" ? `${r.removed}/${r.total}` : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right text-slate-500 font-mono">{fmtDur(r)}</td>
                  <td className="px-2 py-1.5 text-center">
                    {r.status === "queued" && <Badge variant="outline" className="text-slate-500 border-slate-300">Queued</Badge>}
                    {r.status === "uploading" && <Badge className="bg-sky-100 text-sky-700 border-sky-200"><Loader2 className="h-3 w-3 animate-spin mr-1" /> Filtering</Badge>}
                    {r.status === "done" && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Done</Badge>}
                    {r.status === "failed" && <Badge className="bg-rose-100 text-rose-700 border-rose-200">Failed</Badge>}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    {r.status === "done" && (
                      <span className="text-emerald-600 text-[11px]" title="Auto-downloaded during the batch">
                        <Download className="h-3.5 w-3.5 inline" /> saved
                      </span>
                    )}
                  </td>
                </tr>
                {r.status === "failed" && r.error && (
                  <tr className="bg-rose-50/60" data-testid={`bulk-row-${i}-error`}>
                    <td colSpan={7} className="px-3 py-1.5 text-[11px] text-rose-700 border-b border-rose-100">
                      <span className="font-semibold">Error:</span>{" "}
                      <span className="break-all">{r.error}</span>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {!stillRunning && done > 0 && (
        <div className="text-xs text-slate-500">
          Each file was auto-downloaded as soon as it finished (your browser's download bar / <code>chrome://downloads</code>). To re-download one later, re-upload just that single file.
        </div>
      )}
    </div>
  );
}

function AddExclusionDialog({ onClose }) {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [sbc, setSbc] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [searching, setSearching] = React.useState(false);

  React.useEffect(() => {
    const t = setTimeout(async () => {
      if (!q || q.length < 2) { setResults([]); return; }
      setSearching(true);
      try {
        const r = await axios.get(`${API}/commission-exclusions/pan-lookup?q=${encodeURIComponent(q)}`, auth());
        setResults(r.data?.results || []);
      } catch { setResults([]); } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const save = async () => {
    if (!selected || !sbc.trim()) { toast.error("Pick a PAN and enter a sub-broker code"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/commission-exclusions/create`, {
        pan: selected.pan,
        name: selected.name,
        sub_broker_code: sbc.trim(),
        reason: reason.trim(),
      }, auth());
      toast.success("Exclusion added");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-xl" data-testid="add-exclusion-dialog">
        <DialogHeader>
          <DialogTitle>Exclude a client from a sub-broker</DialogTitle>
          <DialogDescription>
            All folios of this PAN that are tagged to the given sub-broker code will be stripped from WBR77 / MFSD205 downloads. Live data is untouched.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium">Find client (PAN or name)</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => { setQ(e.target.value); setSelected(null); }}
                placeholder="AAAPU0926D or Fali Adi"
                className="pl-8"
                data-testid="exclusion-pan-search"
              />
            </div>
            {q.length >= 2 && !selected && (
              <div className="mt-1.5 max-h-48 overflow-y-auto border border-slate-200 rounded-md bg-white">
                {searching ? (
                  <div className="p-2 text-xs text-slate-500 flex items-center gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin" /> Searching…
                  </div>
                ) : results.length === 0 ? (
                  <div className="p-2 text-xs text-slate-400">No matches</div>
                ) : results.map((r) => (
                  <button
                    key={r.pan}
                    type="button"
                    onClick={() => { setSelected(r); setQ(`${r.pan} — ${r.name}`); }}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-sky-50 border-t border-slate-100 first:border-t-0"
                    data-testid={`exclusion-pan-result-${r.pan}`}
                  >
                    <div className="font-mono font-semibold text-slate-800">{r.pan}</div>
                    <div className="text-slate-600">{r.name} <span className="text-slate-400">· {r.folio_count} folios</span></div>
                  </button>
                ))}
              </div>
            )}
            {selected && (
              <div className="mt-1.5 text-xs text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Selected: <b>{selected.pan}</b> · {selected.name} · {selected.folio_count} folios
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs font-medium">Sub-broker code to exclude from</Label>
            <Input
              value={sbc}
              onChange={(e) => setSbc(e.target.value)}
              placeholder="e.g. 1994101"
              className="font-mono"
              data-testid="exclusion-sbc-input"
            />
          </div>
          <div>
            <Label className="text-xs font-medium">Reason (optional)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. moved to another distributor"
              data-testid="exclusion-reason-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !selected || !sbc.trim()} data-testid="exclusion-save-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            Add exclusion
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ────────────────────────────────────────────────────────────────────
// Sub-Broker Tagging — per-folio effective tag editor.
//
// One folio → one active sub-broker at any given month. Same PAN can
// have folios tagged to different brokers (e.g. Fali Adi Unwalla's
// 39 folios split across 1994101 + 1994128). Edits create a new
// effective-period row so historical months keep their prior tag.
function SubBrokerTaggingTab() {
  // A soft password gate protects the whole tab — even a Super-Admin
  // needs to re-authenticate before folio-level payee changes become
  // possible. This is a "second-factor" against accidental changes,
  // not real security; the pin is validated against the current user's
  // own login password via /api/auth/login. Unlock persists for the
  // current tab session only (sessionStorage).
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem("sbt_unlocked") === "1");
  const [pin, setPin] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");

  const handleUnlock = async () => {
    if (!pin) { setError("Enter your password"); return; }
    setVerifying(true);
    setError("");
    try {
      const email = user?.email || "SUPERUSER";
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: pin }),
      });
      if (!r.ok) throw new Error("Wrong password");
      sessionStorage.setItem("sbt_unlocked", "1");
      setUnlocked(true);
      setPin("");
    } catch (e) {
      setError(e.message || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  if (!unlocked) {
    return (
      <div className="max-w-md mx-auto mt-20">
        <div className="rounded-lg border border-amber-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="h-5 w-5 text-amber-600" />
            <h3 className="text-lg font-semibold text-slate-900">Sub-Broker Tagging is locked</h3>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Folio-level payee changes affect the very next brokerage payout run. Re-enter your login password to confirm you meant to open this page.
          </p>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Password</label>
          <input
            type="password"
            value={pin}
            onChange={(e) => { setPin(e.target.value); setError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") handleUnlock(); }}
            placeholder="Enter your login password"
            className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid="sbt-password-input"
            autoFocus
          />
          {error && <p className="mt-2 text-xs text-rose-600">{error}</p>}
          <Button
            onClick={handleUnlock}
            disabled={verifying || !pin}
            className="mt-4 w-full bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="sbt-unlock-btn"
          >
            {verifying ? <><Loader2 className="mr-2 h-4 w-4 animate-spin"/> Verifying…</> : <><Unlock className="mr-2 h-4 w-4"/> Unlock</>}
          </Button>
        </div>
      </div>
    );
  }

  // Sub-Broker Tagging renders the Client-Wise Summary layout locked
  // to the "All Clients" tab, hides the outer header/AUM cards/view
  // tabs, and swaps the Associate-Code edit popover for the folio-level
  // "From → To" reassignment dialog.
  return <ClientWise restrictToTab="all" associateEditMode="folio-modal" hideHeader />;
}
